/* Liftoff Math engine: topic graph, problem templates, answer checking,
 * mastery + spaced review, and the test-week planner.
 * ESM adapter of the independently checked supplied prototype.
 * Summit uses math/learning.ts for learning state and scheduling. */
export function createAlgebraEngine() {
  'use strict';

  /* ---------------- rationals ---------------- */
  function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
  function lcm(a, b) { return Math.abs(a * b) / gcd(a, b); }
  function R(n, d) {
    if (d === undefined) d = 1;
    if (!Number.isSafeInteger(n) || !Number.isSafeInteger(d)) throw new Error('Use smaller exact numbers');
    if (d === 0) throw new Error('division by zero');
    if (d < 0) { n = -n; d = -d; }
    const g = gcd(n, d);
    return { n: (n / g) || 0, d: d / g };
  }
  const radd = (a, b) => R(a.n * b.d + b.n * a.d, a.d * b.d);
  const rsub = (a, b) => R(a.n * b.d - b.n * a.d, a.d * b.d);
  const rmul = (a, b) => R(a.n * b.n, a.d * b.d);
  const rdiv = (a, b) => { if (b.n === 0) throw new Error('division by zero'); return R(a.n * b.d, a.d * b.n); };
  const req = (a, b) => a.n === b.n && a.d === b.d;
  const rval = a => a.n / a.d;
  function rpow(a, k) { let r = R(1); for (let i = 0; i < Math.abs(k); i++) r = rmul(r, a); return k < 0 ? rdiv(R(1), r) : r; }

  /* ---------------- polynomials ----------------
   * A polynomial is {key: {c: R, m: {var: exp}}}. key '' is the constant. */
  function monoKey(m) { return Object.keys(m).sort().map(v => v + (m[v] > 1 ? '^' + m[v] : '')).join('*'); }
  function monoDeg(m) { let d = 0; for (const v in m) d += m[v]; return d; }
  function monoLex(m) { return Object.keys(m).sort().map(v => v.repeat(m[v])).join(''); }
  // Display order: highest degree first, with a consistent alphabetical tie-break.
  // Answer checking accepts any order among terms of the same total degree.
  function cmpMono(a, b) {
    const d = monoDeg(b) - monoDeg(a);
    if (d) return d;
    const la = monoLex(a), lb = monoLex(b);
    return la < lb ? -1 : la > lb ? 1 : 0;
  }
  const P = {
    zero: () => ({}),
    c(r) { r = typeof r === 'number' ? R(r) : r; return r.n === 0 ? {} : { '': { c: r, m: {} } }; },
    v(name, e) { const m = {}; m[name] = e || 1; return { [monoKey(m)]: { c: R(1), m } }; },
    term(c, m) { c = typeof c === 'number' ? R(c) : c; const mm = {}; for (const k in m) if (m[k]) mm[k] = m[k]; return c.n === 0 ? {} : { [monoKey(mm)]: { c, m: mm } }; },
    add(p, q) {
      const out = {};
      for (const k in p) out[k] = { c: p[k].c, m: p[k].m };
      for (const k in q) {
        if (out[k]) { const c = radd(out[k].c, q[k].c); if (c.n === 0) delete out[k]; else out[k] = { c, m: out[k].m }; }
        else out[k] = { c: q[k].c, m: q[k].m };
      }
      return out;
    },
    scale(p, r) { r = typeof r === 'number' ? R(r) : r; const out = {}; if (r.n === 0) return out; for (const k in p) out[k] = { c: rmul(p[k].c, r), m: p[k].m }; return out; },
    neg(p) { return P.scale(p, R(-1)); },
    sub(p, q) { return P.add(p, P.neg(q)); },
    mul(p, q) {
      if (Object.keys(p).length * Object.keys(q).length > 4096) throw new ParseError('expression too large');
      let out = {};
      for (const a in p) for (const b in q) {
        const m = Object.assign({}, p[a].m);
        for (const v in q[b].m) m[v] = (m[v] || 0) + q[b].m[v];
        out = P.add(out, P.term(rmul(p[a].c, q[b].c), m));
      }
      return out;
    },
    pow(p, n) { let r = P.c(1); for (let i = 0; i < n; i++) r = P.mul(r, p); return r; },
    isConst(p) { const ks = Object.keys(p); return ks.length === 0 || (ks.length === 1 && ks[0] === ''); },
    constVal(p) { return p[''] ? p[''].c : R(0); },
    eq(p, q) {
      const a = Object.keys(p), b = Object.keys(q);
      if (a.length !== b.length) return false;
      for (const k of a) if (!q[k] || !req(p[k].c, q[k].c)) return false;
      return true;
    },
    terms(p) { return Object.keys(p).map(k => p[k]).sort((s, t) => cmpMono(s.m, t.m)); },
    degree(p) { let d = 0; for (const k in p) d = Math.max(d, monoDeg(p[k].m)); return d; },
    evalAt(p, env) {
      let s = 0;
      for (const k in p) { let t = rval(p[k].c); for (const v in p[k].m) t *= Math.pow(env[v], p[k].m[v]); s += t; }
      return s;
    },
    vars(p) { const s = new Set(); for (const k in p) for (const v in p[k].m) s.add(v); return [...s].sort(); },
  };
  // Build from a list of [coef, monomial-object] pairs.
  function poly(list) { let p = {}; for (const [c, m] of list) p = P.add(p, P.term(c, m || {})); return p; }
  // Linear helper: a·v + b
  function lin(a, b, v) { return poly([[a, { [v || 'x']: 1 }], [b, {}]]); }

  /* ---------------- parsing student input ---------------- */
  function normalize(src) {
    return String(src)
      .replace(/[−–—]/g, '-').replace(/[×·•∙]/g, '*').replace(/÷/g, '/')
      .replace(/²/g, '^2').replace(/³/g, '^3').replace(/⁴/g, '^4').replace(/⁵/g, '^5').replace(/⁶/g, '^6')
      .replace(/\*\*/g, '^')
      .replace(/[\[{]/g, '(').replace(/[\]}]/g, ')')
      .toLowerCase();
  }
  class ParseError extends Error {}
  function tokenize(s) {
    const toks = []; let i = 0;
    while (i < s.length) {
      const ch = s[i];
      if (/\s/.test(ch)) { i++; continue; }
      if (/[0-9.]/.test(ch)) {
        let j = i; while (j < s.length && /[0-9.]/.test(s[j])) j++;
        const txt = s.slice(i, j);
        if (!/^(\d+(\.\d+)?|\.\d+)$/.test(txt)) throw new ParseError('bad number');
        let v;
        if (txt.includes('.')) { const [w, f] = txt.split('.'); const d = Math.pow(10, f.length); v = R(parseInt(w || '0', 10) * d + parseInt(f, 10), d); }
        else v = R(parseInt(txt, 10));
        toks.push({ t: 'num', v }); i = j; continue;
      }
      if (/[a-zλ]/.test(ch)) { toks.push({ t: 'var', v: ch }); i++; continue; }
      if ('+-*/^()'.includes(ch)) { toks.push({ t: ch }); i++; continue; }
      throw new ParseError('unexpected character ' + ch);
    }
    return toks;
  }
  function parseTokens(toks) {
    let i = 0;
    const peek = () => toks[i], next = () => toks[i++];
    function expr() {
      let v = term();
      while (peek() && (peek().t === '+' || peek().t === '-')) {
        const op = next().t; const r = term();
        v = op === '+' ? P.add(v, r) : P.sub(v, r);
      }
      return v;
    }
    function term() {
      let v = unary();
      for (;;) {
        const p = peek();
        if (!p) break;
        if (p.t === '*') { next(); v = P.mul(v, unary()); }
        else if (p.t === '/') {
          next(); const d = unary();
          if (!P.isConst(d)) throw new ParseError('can only divide by a number');
          const dv = P.constVal(d); if (dv.n === 0) throw new ParseError('division by zero');
          v = P.scale(v, rdiv(R(1), dv));
        }
        else if (p.t === 'num' || p.t === 'var' || p.t === '(') v = P.mul(v, power());
        else break;
      }
      return v;
    }
    function unary() {
      const p = peek();
      if (p && (p.t === '-' || p.t === '+')) { next(); const v = unary(); return p.t === '-' ? P.neg(v) : v; }
      return power();
    }
    function power() {
      const b = atom();
      if (peek() && peek().t === '^') {
        next();
        let neg = false;
        if (peek() && peek().t === '-') { next(); neg = true; }
        let e = next();
        if (e && e.t === '(') { const inner = next(); const close = next(); if (!close || close.t !== ')') throw new ParseError('bad exponent'); e = inner; }
        if (!e || e.t !== 'num' || e.v.d !== 1) throw new ParseError('exponent must be a whole number');
        const n = e.v.n * (neg ? -1 : 1);
        if (Math.abs(n) > 40) throw new ParseError('exponent too large');
        if (n < 0) { if (!P.isConst(b)) throw new ParseError('negative exponent on a variable'); return P.c(rpow(P.constVal(b), n)); }
        return P.pow(b, n);
      }
      return b;
    }
    function atom() {
      const p = next();
      if (!p) throw new ParseError('incomplete');
      if (p.t === 'num') return P.c(p.v);
      if (p.t === 'var') return P.v(p.v);
      if (p.t === '(') { const v = expr(); const c = next(); if (!c || c.t !== ')') throw new ParseError('missing )'); return v; }
      throw new ParseError('unexpected ' + p.t);
    }
    const v = expr();
    if (i !== toks.length) throw new ParseError('extra input');
    return v;
  }
  function parsePoly(src) {
    if (typeof src !== 'string' && typeof src !== 'number') throw new ParseError('expression expected');
    const text = String(src);
    if (!text.trim() || text.length > 512) throw new ParseError('enter a shorter expression');
    return parseTokens(tokenize(normalize(text)));
  }

  // Is the typed polynomial written in simplified standard form?
  function standardCheck(src) {
    // Redundant enclosing parentheses and grouped numeric coefficients do not
    // change standard form. Keep parentheses that actually group an expression
    // for multiplication or require distribution.
    src=String(src).trim();
    for (;;) {
      if (!src.startsWith('(')||!src.endsWith(')')) break;
      let depth=0,whole=true;
      for(let i=0;i<src.length;i++){if(src[i]==='(')depth++;if(src[i]===')')depth--;if(depth===0&&i<src.length-1){whole=false;break;}}
      if(!whole||depth!==0)break;src=src.slice(1,-1).trim();
    }
    src=src.replace(/\(\s*([+-]?\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?)\s*\)/g,'$1');
    const toks = tokenize(normalize(src));
    const chunks = []; let cur = []; let depth = 0;
    toks.forEach((t, idx) => {
      if (t.t === '(') depth++;
      if (t.t === ')') depth--;
      const prev = toks[idx - 1];
      const binary = prev && (prev.t === 'num' || prev.t === 'var' || prev.t === ')');
      if (depth === 0 && (t.t === '+' || t.t === '-') && binary) { chunks.push(cur); cur = [t]; }
      else cur.push(t);
    });
    chunks.push(cur);
    const monos = [];
    for (const c of chunks) {
      if (c.some(t => t.t === '(')) return { ok: false, reason: 'notSimplified' };
      const p = parseTokens(c);
      const ks = Object.keys(p);
      if (ks.length === 0) continue; // a "+ 0" term
      if (ks.length !== 1) return { ok: false, reason: 'notSimplified' };
      monos.push(p[ks[0]].m);
    }
    const keys = monos.map(monoKey);
    if (new Set(keys).size !== keys.length) return { ok: false, reason: 'notSimplified' };
    for (let k = 1; k < monos.length; k++) if (monoDeg(monos[k - 1]) < monoDeg(monos[k])) return { ok: false, reason: 'order' };
    return { ok: true };
  }

  function parseNumber(src) {
    let s = normalize(src).trim();
    s = s.replace(/^[a-z]\s*=\s*/, '');
    s = s.replace(/\b(square|sq\.?|cubic)\b/g, '');
    s = s.replace(/\$/g, '').replace(/\b(feet|foot|ft|inches|inch|in|meters|meter|m|centimeters|centimeter|cm|yards|yard|yd|miles|mile|mi|units|unit|degrees|degree|seconds|second|sec|s|minutes|minute|min|hours|hour|hrs|hr|days|day|weeks|week|years|year|yrs|dollars|dollar|cents|cent|liters|liter|l|gallons|gallon|gal|ounces|ounce|oz|pounds|pound|lbs|lb|grams|gram|g|people|students|tickets|bacteria|cells)\b\.?/g, '');
    // Unwrap a teacher-style answer such as "(4) feet", without destroying
    // parentheses inside arithmetic expressions like (1 + 2) * 3.
    const wrapped = s.match(/^\(\s*([+-]?\d+(?:\.\d+)?(?:\s+\d+\s*\/\s*\d+|\s*\/\s*\d+)?)\s*\)$/);
    if (wrapped) s = wrapped[1];
    const mixed = s.match(/^(-?)(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
    if (mixed) { const w = parseInt(mixed[2], 10), n = parseInt(mixed[3], 10), d = parseInt(mixed[4], 10); const r = R(w * d + n, d); return mixed[1] ? R(-r.n, r.d) : r; }
    if (/[a-z]/.test(normalize(s))) throw new ParseError('number expected');
    const p = parsePoly(s);
    if (!P.isConst(p)) throw new ParseError('number expected');
    return P.constVal(p);
  }

  /* ---------------- formatting (HTML) ---------------- */
  const MINUS = '−';
  const frac = (a, b) => `<span class="fr"><span>${a}</span><span>${b}</span></span>`;
  function fmtR(r) {
    if (r.d === 1) return (r.n < 0 ? MINUS : '') + Math.abs(r.n);
    return (r.n < 0 ? MINUS : '') + frac(Math.abs(r.n), r.d);
  }
  // Exact decimal text for a rational with a terminating expansion of at most maxPlaces places.
  function decimalText(r, maxPlaces = 10) {
    if (!r || !Number.isSafeInteger(r.n) || !Number.isSafeInteger(r.d) || r.d <= 0) return null;
    let d = r.d, twos = 0, fives = 0;
    while (d % 2 === 0) { d /= 2; twos++; }
    while (d % 5 === 0) { d /= 5; fives++; }
    const places = Math.max(twos, fives);
    if (d !== 1 || places > maxPlaces) return null;
    const digits = (BigInt(Math.abs(r.n)) * 10n ** BigInt(places) / BigInt(r.d)).toString().padStart(places + 1, '0');
    return (r.n < 0 ? MINUS : '') + (places ? digits.slice(0, -places) + '.' + digits.slice(-places) : digits);
  }
  // A decimal problem gives decimal operands, money, a percent, a rounding instruction or a
  // decimal place name, and no fraction. Its answers and worked steps are shown as decimals;
  // a problem about fractions keeps its fractions. Display only: grading compares values.
  function decimalContext(p) {
    // An explicit instruction to round or to answer as a decimal wins over fractions among the givens.
    const ask = toText(p.ask ?? '');
    if (/\bfractions?\b|numerator|denominator|lowest terms|mixed number/i.test(ask)) return false;
    if (/\bround|\bdecimal\b|\bdecimal places?\b|\bnearest (?:tenth|hundredth|thousandth|cent)\b/i.test(ask)) return true;
    // Otherwise every text node counts, including table cells and diagram labels.
    const given = `${p.ask ?? ''} ${p.math ?? ''}`.replace(/<span class="fr">/g, ' 1/2 ').replace(/<[^>]+>/g, ' ');
    if (/\bfractions?\b|numerator|denominator|lowest terms|mixed number|\d\s*\/\s*\d|[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/i.test(given) || /\bratios?\b/i.test(ask)) return false;
    return /\$\s?\d|\d\.\d|%|\bround|\bdecimal|\b(?:tenths?|hundredths?|thousandths?)\b/i.test(given);
  }
  const fmtDec = r => decimalText(r) ?? fmtR(r);
  // Stacked numeric fractions with a terminating value become decimals (mixed numbers are left alone).
  // So do the plain-text "(a)/(b)" fractions that toText produces for tables and steps,
  const decimalizeHtml = html => typeof html !== 'string' ? html : html
    .replace(/(?<![\d>])<span class="fr"><span>(\d+)<\/span><span>(\d+)<\/span><\/span>/g, (all, n, d) => decimalText(R(Number(n), Number(d))) ?? all)
    .replace(/(?<![\w)])\(([−-]?\d+)\)\/\((\d+)\)(?![\w(])/g, (all, n, d) => decimalText(R(Number(n.replace('−', '-')), Number(d))) ?? all)
    // and plain a/b written in a step's text ("= 644/25" → "= 25.76"), outside tags.
    .split(/(<[^>]+>)/).map(part => part.startsWith('<') ? part : part.replace(/(?<![\w./)^])(\d+)\/(\d+)(?![\w(/^]|\.\d)/g, (all, n, d) => decimalText(R(Number(n), Number(d))) ?? all)).join('');
  // Apply the decimal presentation to a generated problem in place.
  function presentDecimals(p) {
    // Numeric answers only: an equation or expression answer keeps its exact coefficients.
    if (!p || !['num', 'angle', 'pair', 'set'].includes(p.answer?.type) || !decimalContext(p)) return p;
    p.decimal = true;
    if (typeof p.answerHtml === 'string') p.answerHtml = decimalizeHtml(p.answerHtml);
    if (Array.isArray(p.steps)) p.steps = p.steps.map(decimalizeHtml);
    if (Array.isArray(p.fullsteps)) p.fullsteps = p.fullsteps.map(decimalizeHtml);
    return p;
  }
  const fmtN = n => (n < 0 ? MINUS : '') + Math.abs(n);
  const par = n => n < 0 ? '(' + fmtN(n) + ')' : fmtN(n);
  function fmtMono(m) { return Object.keys(m).sort().map(v => `<i>${v}</i>` + (m[v] > 1 ? `<sup>${m[v]}</sup>` : '')).join(''); }
  // Format terms in the given order (default: standard form).
  function fmtTerms(list) {
    if (!list.length) return '0';
    return list.map((t, idx) => {
      const neg = t.c.n < 0; const abs = R(Math.abs(t.c.n), t.c.d);
      const hasVar = Object.keys(t.m).length > 0;
      let body;
      if (!hasVar) body = fmtR(abs);
      else if (abs.n === 1 && abs.d === 1) body = fmtMono(t.m);
      else if (abs.d !== 1 && abs.n === 1) body = frac(fmtMono(t.m), abs.d);
      else body = fmtR(abs) + fmtMono(t.m);
      if (idx === 0) return (neg ? MINUS : '') + body;
      return (neg ? ' ' + MINUS + ' ' : ' + ') + body;
    }).join('');
  }
  const fmtP = p => fmtTerms(P.terms(p));
  const joinP = (a, b) => { const t = fmtP(b); return fmtP(a) + (t.startsWith(MINUS) ? ' ' + MINUS + ' ' + t.slice(1) : ' + ' + t); };
  function fmtFactor(p) { return '(' + fmtP(p) + ')'; }
  // Plain text for answer keys / notes.
  function toText(html) {
    return String(html)
      .replace(/<span class="fr"><span>(.*?)<\/span><span>(.*?)<\/span><\/span>/g, '($1)/($2)')
      .replace(/<sup>(.*?)<\/sup>/g, '^$1')
      .replace(/<svg[\s\S]*?<\/svg>/g, '[diagram]')
      .replace(/<table[\s\S]*?<\/table>/g, '[table]')
      .replace(/<br\s*\/?>/g, ' ')
      .replace(/<\/?[a-z][^>]*>/gi, '')
      .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      .replace(/−/g, '-').replace(/\s+/g, ' ').trim();
  }
  // Plain input string a student could type for a polynomial (used by the harness).
  function typeP(p) {
    const ts = P.terms(p);
    if (!ts.length) return '0';
    return ts.map((t, idx) => {
      const neg = t.c.n < 0; const abs = R(Math.abs(t.c.n), t.c.d);
      const vars = Object.keys(t.m).sort().map(v => v + (t.m[v] > 1 ? '^' + t.m[v] : '')).join('');
      let coef = abs.d === 1 ? String(abs.n) : abs.n + '/' + abs.d;
      if (vars && abs.n === 1 && abs.d === 1) coef = '';
      const body = coef + (vars && abs.d !== 1 ? '' : '') + vars;
      return (idx === 0 ? (neg ? '-' : '') : (neg ? ' - ' : ' + ')) + body;
    }).join('');
  }
  // JS source for evaluating a polynomial (harness: independent check).
  function jsP(p) {
    const ts = P.terms(p);
    if (!ts.length) return '0';
    return ts.map(t => `(${t.c.n}/${t.c.d})` + Object.keys(t.m).map(v => `*Math.pow(${v},${t.m[v]})`).join('')).join('+');
  }

  /* ---------------- randomness ---------------- */
  function mulberry32(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function helpers(rng) {
    const H = {
      rng,
      ri: (a, b) => a + Math.floor(rng() * (b - a + 1)),
      nz: (a, b) => { let v; do v = a + Math.floor(rng() * (b - a + 1)); while (v === 0); return v; },
      pick: arr => arr[Math.floor(rng() * arr.length)],
      coin: p => rng() < (p === undefined ? 0.5 : p),
      shuffle: arr => { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; },
      retry(fn, n) { for (let k = 0; k < (n || 400); k++) { const v = fn(); if (v != null) return v; } throw new Error('generator could not satisfy constraints'); },
    };
    return H;
  }
  // Multiple-choice helper: correct + distractors -> shuffled, de-duplicated choices.
  function mc(H, correct, distractors) {
    const seen = new Set([toText(correct.html)]);
    const opts = [{ html: correct.html, note: null, ok: true }];
    for (const d of distractors) {
      const key = toText(d.html);
      if (seen.has(key)) continue;
      seen.add(key); opts.push({ html: d.html, note: d.note || null, ok: false });
      if (opts.length === 4) break;
    }
    const sh = H.shuffle(opts);
    return { type: 'mc', choices: sh.map(o => o.html), notes: sh.map(o => o.note), index: sh.findIndex(o => o.ok) };
  }

  /* ---------------- diagrams ---------------- */
  function tilesSVG(a, b, c, d) {
    // (a x + b) across the top, (c x + d) down the side. x = 56px, 1 = 14px.
    const X = 56, U = 14, G = 3, pad = 8, hdr = 16;
    const colsW = []; for (let i = 0; i < a; i++) colsW.push(['x', X]); for (let i = 0; i < b; i++) colsW.push(['1', U]);
    const rowsH = []; for (let i = 0; i < c; i++) rowsH.push(['x', X]); for (let i = 0; i < d; i++) rowsH.push(['1', U]);
    const W = colsW.reduce((s, w) => s + w[1] + G, 0), Hh = rowsH.reduce((s, h) => s + h[1] + G, 0);
    const ox = pad + hdr + G * 2, oy = pad + hdr + G * 2;
    let out = `<svg class="tiles" viewBox="0 0 ${ox + W + pad} ${oy + Hh + pad}" role="img" aria-label="Algebra tiles">`;
    out += `<line x1="${ox - G}" y1="${oy - G}" x2="${ox + W}" y2="${oy - G}" class="t-rule"/><line x1="${ox - G}" y1="${oy - G}" x2="${ox - G}" y2="${oy + Hh}" class="t-rule"/>`;
    let x = ox;
    for (const [k, w] of colsW) { out += `<rect x="${x}" y="${pad}" width="${w}" height="${hdr}" class="${k === 'x' ? 't-x' : 't-1'}" rx="2"/>`; x += w + G; }
    let y = oy;
    for (const [k, h] of rowsH) { out += `<rect x="${pad}" y="${y}" width="${hdr}" height="${h}" class="${k === 'x' ? 't-x' : 't-1'}" rx="2"/>`; y += h + G; }
    y = oy;
    for (const [rk, h] of rowsH) {
      x = ox;
      for (const [ck, w] of colsW) {
        const cls = rk === 'x' && ck === 'x' ? 't-x2' : (rk === 'x' || ck === 'x') ? 't-x' : 't-1';
        out += `<rect x="${x}" y="${y}" width="${w}" height="${h}" class="${cls}" rx="2"/>`;
        x += w + G;
      }
      y += h + G;
    }
    return out + '</svg>';
  }
  function rectSVG(topLabel, sideLabel, opts) {
    opts = opts || {};
    const w = opts.square ? 120 : 190, h = opts.square ? 120 : 110;
    let s = `<svg class="figure" viewBox="0 0 ${w + 150} ${h + 60}" role="img" aria-label="Figure">`;
    s += `<rect x="20" y="30" width="${w}" height="${h}" class="f-shape"/>`;
    if (opts.cut) s += `<rect x="${20 + w - 44}" y="30" width="44" height="44" class="f-cut"/><text x="${20 + w - 22}" y="58" class="f-lbl f-small" text-anchor="middle">${opts.cut}</text>`;
    s += `<text x="${20 + w / 2}" y="20" class="f-lbl" text-anchor="middle">${topLabel}</text>`;
    s += `<text x="${30 + w}" y="${30 + h / 2 + 5}" class="f-lbl">${sideLabel}</text>`;
    return s + '</svg>';
  }
  function triSVG(baseLabel, hLabel) {
    let s = `<svg class="figure" viewBox="0 0 330 170" role="img" aria-label="Triangle">`;
    s += `<polygon points="20,140 220,140 90,25" class="f-shape"/><line x1="90" y1="25" x2="90" y2="140" class="f-dash"/>`;
    s += `<text x="120" y="162" class="f-lbl" text-anchor="middle">${baseLabel}</text><text x="98" y="95" class="f-lbl">${hLabel}</text>`;
    return s + '</svg>';
  }
  // Shaded-area figure: the outer shape is shaded and the inner one is the unshaded cut-out.
  // A frame centers the inner rectangle; otherwise it sits inside the lower-right area.
  function shadedSVG(outerTop, outerSide, innerTop, innerSide, opts) {
    opts = opts || {};
    const w = opts.square ? 150 : 210, h = opts.square ? 150 : 130;
    const iw = opts.frame ? w - 48 : opts.square ? 74 : opts.squareHole ? 66 : 100, ih = opts.frame ? h - 48 : opts.square ? 74 : opts.squareHole ? 66 : 62;
    const ix = opts.frame ? 20 + (w - iw) / 2 : 20 + w - iw - 22, iy = opts.frame ? 30 + (h - ih) / 2 : 30 + h - ih - 20;
    let s = `<svg class="figure" viewBox="0 0 ${w + 150} ${h + 60}" role="img" aria-label="Shaded figure">`;
    s += `<rect x="20" y="30" width="${w}" height="${h}" class="f-shape"/><rect x="${ix}" y="${iy}" width="${iw}" height="${ih}" class="f-cut"/>`;
    s += `<text x="${20 + w / 2}" y="20" class="f-lbl" text-anchor="middle">${outerTop}</text>`;
    s += `<text x="${30 + w}" y="${30 + h / 2 + 5}" class="f-lbl">${outerSide}</text>`;
    s += `<text x="${ix + iw / 2}" y="${iy + 18}" class="f-lbl f-small" text-anchor="middle">${innerTop}</text>`;
    s += `<text x="${ix + iw / 2}" y="${iy + ih / 2 + 12}" class="f-lbl f-small" text-anchor="middle">${innerSide}</text>`;
    return s + '</svg>';
  }
  const svgText = html => toText(html).replace(/\^(\d)/g, (m, d) => '²³⁴⁵'[+d - 2] || m);

  /* ---------------- topic graph ---------------- */
  const UNITS = [
    { id: 'u1', name: 'Unit 1: Equations & Inequalities' },
    { id: 'u2', name: 'Unit 2: Exponents & Polynomials' },
    { id: 'u3', name: 'Unit 3: Functions' },
    { id: 'u4', name: 'Unit 4: Linear Functions' },
    { id: 'u5', name: 'Unit 5: Systems' },
    { id: 'u6', name: 'Unit 6: Radicals & Rational Exponents' },
    { id: 'u7', name: 'Unit 7: Factoring' },
    { id: 'u8', name: 'Unit 8: Quadratics' },
    { id: 'u9', name: 'Unit 9: Exponential Functions' },
  ];
  const TOPICS = [];
  const byId = {};
  const order = {};
  // Registration order breaks ties; prerequisites always come first. Recomputed
  // whenever the graph changes, so a unit module may hook into earlier topics.
  // The recomputation is lazy: a course can register hundreds of topics, and
  // sorting the whole graph after each one made Worker startup quadratic.
  let orderDirty = false;
  const ensureOrder = () => { if (orderDirty) reorder(); };
  const ORDER = new Proxy(order, {
    get: (target, key) => { ensureOrder(); return target[key]; },
    has: (target, key) => { ensureOrder(); return key in target; },
    ownKeys: target => { ensureOrder(); return Reflect.ownKeys(target); },
    getOwnPropertyDescriptor: (target, key) => { ensureOrder(); return Reflect.getOwnPropertyDescriptor(target, key); },
  });
  function reorder() {
    const seen = new Set(), out = [], visiting = new Set();
    const visit = id => {
      if (seen.has(id)) return;
      if (visiting.has(id)) throw new Error('prerequisite cycle at ' + id);
      visiting.add(id);
      for (const p of byId[id].prereqs) { if (!byId[p]) throw new Error(id + ': unknown prerequisite ' + p); visit(p); }
      visiting.delete(id); seen.add(id); out.push(id);
    };
    for (const t of TOPICS.slice().sort((a, b) => UNITS.findIndex(u => u.id === a.unit) - UNITS.findIndex(u => u.id === b.unit))) visit(t.id);
    for (const k in order) delete order[k];
    out.forEach((id, i) => { order[id] = i; });
    orderDirty = false;
  }
  let deferOrder = true;
  function topic(def) {
    if (byId[def.id]) throw new Error('duplicate topic ' + def.id);
    def.prereqs = def.prereqs || [];
    TOPICS.push(def); byId[def.id] = def;
    if (!deferOrder) orderDirty = true;
  }
  function addPrereq(id, prereq) { const t = byId[id]; if (!t.prereqs.includes(prereq)) t.prereqs.push(prereq); orderDirty = true; }
  function unit(def) { if (!UNITS.some(u => u.id === def.id)) UNITS.push(def); }
  const X = { x: 1 }, X2 = { x: 2 };

  topic({
    id: 'int_ops', unit: 'u1', name: 'Integer operations', prereqs: [],
    idea: 'Same signs: add the sizes and keep the sign. Different signs: subtract the sizes and keep the sign of the bigger one. Subtracting a number means adding its opposite. When multiplying, same signs give a positive and different signs give a negative.',
    gen(H) {
      const a = H.nz(-15, 15), b = H.nz(-12, 12), op = H.pick(['+', '-', '*']);
      const ans = op === '+' ? a + b : op === '-' ? a - b : a * b;
      const sym = op === '+' ? '+' : op === '-' ? MINUS : '·';
      const steps = [];
      const traps = [];
      if (op === '-') {
        steps.push(`Subtracting is adding the opposite: ${fmtN(a)} ${MINUS} ${par(b)} = ${fmtN(a)} + ${par(-b)}`);
        if (b < 0) traps.push({ value: R(a + b), note: 'Subtracting a negative is the same as adding: −(−5) becomes +5.' });
      } else if (op === '+') {
        steps.push((a < 0) === (b < 0) ? 'Same signs: add the sizes and keep the sign.' : 'Different signs: subtract the sizes and keep the sign of the larger size.');
      } else {
        steps.push((a < 0) === (b < 0) ? 'Same signs multiply to a positive.' : 'Different signs multiply to a negative.');
        traps.push({ value: R(-ans), note: 'Check the sign: same signs give a positive, different signs give a negative.' });
      }
      steps.push(`= ${fmtN(ans)}`);
      return { ask: 'Compute.', math: `${fmtN(a)} ${sym} ${par(b)}`, answer: { type: 'num', value: R(ans) }, steps, traps,
        teachingIdea: op === '*' ? 'Multiply the magnitudes, then determine the sign: matching signs give a positive product; different signs give a negative product.' : op === '-' ? 'Rewrite subtraction as adding the opposite. Then add signed numbers: matching signs add magnitudes; different signs subtract magnitudes.' : 'For matching signs, add the magnitudes and keep the sign. For different signs, subtract the magnitudes and use the sign of the larger magnitude.',
        pitfalls: op === '*' ? ['Multiply the magnitudes; do not add them.', 'Use the multiplication sign rule, even when both factors are negative.'] : ['Keep the operation and each number’s sign separate.', 'Compare magnitudes, not which signed number is greater.'],
        check: { kind: 'num', js: `(${a})${op === '*' ? '*' : op}(${b})` } };
    },
  });

  topic({
    id: 'exp_neg', unit: 'u2', name: 'Exponents and parentheses', prereqs: ['int_ops'],
    idea: 'An exponent only applies to what is directly underneath it. (−4)² = (−4)(−4) = 16, because the parentheses put the negative under the exponent. −4² = −1·4² = −16, because only the 4 is squared.',
    gen(H) {
      const shape = H.pick(['paren', 'noparen', 'combo', 'cube', 'scaled', 'negneg']);
      if (shape === 'scaled') {
        const k = H.pick([2, 3, 4, 5, -2, -3]), b = H.ri(2, 6), paren = H.coin();
        const ans = paren ? k * b * b : -k * b * b;
        // Without parentheses the square still binds first; group −b² so "· −" never reads as one sign.
        const shown = paren ? `${fmtN(k)}(${MINUS}${b})<sup>2</sup>` : `${fmtN(k)} · (${MINUS}${b}<sup>2</sup>)`;
        return {
          ask: 'Simplify.', math: shown, answer: { type: 'num', value: R(ans) },
          steps: [paren ? `(${MINUS}${b})<sup>2</sup> = ${b * b}` : `${MINUS}${b}<sup>2</sup> = ${MINUS}1 · ${b * b} = ${fmtN(-b * b)}`, 'Exponents come before multiplication.', `${fmtN(k)} · ${paren ? b * b : par(-b * b)} = ${fmtN(ans)}`],
          traps: [{ value: R(-ans), note: paren ? `The parentheses make (${MINUS}${b})² positive.` : `Without parentheses only the ${b} is squared, so ${MINUS}${b}² = ${fmtN(-b * b)}.` }, { value: R(k * k * b * b * (paren ? 1 : -1)), note: 'Do the exponent first, then multiply by the number in front.' }],
          check: { kind: 'num', js: paren ? `(${k})*(-${b})**2` : `(${k})*(-(${b}**2))` },
        };
      }
      if (shape === 'negneg') {
        const b = H.ri(2, 6), e = H.pick([2, 3]);
        const ans = -Math.pow(-b, e);
        return {
          ask: 'Simplify.', math: `${MINUS}(${MINUS}${b})<sup>${e}</sup>`, answer: { type: 'num', value: R(ans) },
          steps: [`Inside first: (${MINUS}${b})<sup>${e}</sup> = ${fmtN(Math.pow(-b, e))}`, `Then the outside negative: ${MINUS}(${fmtN(Math.pow(-b, e))}) = ${fmtN(ans)}`],
          traps: [{ value: R(-ans), note: 'Square (or cube) what is inside the parentheses first, then apply the negative in front.' }],
          check: { kind: 'num', js: `-((-${b})**${e})` },
        };
      }
      if (shape === 'combo') {
        const a = H.ri(2, 6), b = H.ri(2, 6);
        const ans = -(a * a) + b * b;
        return {
          ask: 'Simplify.', math: `${MINUS}${a}<sup>2</sup> + (${MINUS}${b})<sup>2</sup>`, answer: { type: 'num', value: R(ans) },
          steps: [`${MINUS}${a}<sup>2</sup> = ${MINUS}1 · ${a}<sup>2</sup> = ${fmtN(-a * a)}`, `(${MINUS}${b})<sup>2</sup> = (${MINUS}${b})(${MINUS}${b}) = ${b * b}`, `${fmtN(-a * a)} + ${b * b} = ${fmtN(ans)}`],
          traps: [{ value: R(a * a + b * b), note: `${MINUS}${a}² is negative: only the ${a} is squared, then the negative is applied.` }],
          check: { kind: 'num', js: `-(${a}**2)+(-${b})**2` },
        };
      }
      const b = H.ri(2, 7);
      const e = shape === 'cube' ? 3 : H.pick([2, 2, 2, 3, 4].filter(k => Math.pow(b, k) <= 400));
      if (shape === 'paren' || shape === 'cube') {
        const ans = Math.pow(-b, e);
        const traps = [{ value: R(b * e * (ans < 0 ? -1 : 1)), note: 'An exponent means repeated multiplication, not base × exponent.' }];
        if (e % 2 === 0) traps.push({ value: R(-ans), note: `The parentheses put the negative under the exponent: (${MINUS}${b})(${MINUS}${b}) is positive.` });
        return {
          ask: 'Simplify.', math: `(${MINUS}${b})<sup>${e}</sup>`, answer: { type: 'num', value: R(ans) },
          steps: [`(${MINUS}${b})<sup>${e}</sup> = ` + Array(e).fill(`(${MINUS}${b})`).join('') , `= ${fmtN(ans)}` + (e % 2 === 0 ? ' (an even number of negatives makes a positive)' : ' (an odd number of negatives stays negative)')],
          traps, check: { kind: 'num', js: `(-${b})**${e}` },
        };
      }
      const ans = -Math.pow(b, e);
      return {
        ask: 'Simplify.', math: `${MINUS}${b}<sup>${e}</sup>`, answer: { type: 'num', value: R(ans) },
        steps: [`Only the ${b} is under the exponent: ${MINUS}${b}<sup>${e}</sup> = ${MINUS}1 · ${b}<sup>${e}</sup>`, `= ${MINUS}1 · ${Math.pow(b, e)} = ${fmtN(ans)}`],
        traps: [e % 2 === 0 ? { value: R(-ans), note: `Without parentheses the negative stays outside the power: ${MINUS}${b}<sup>${e}</sup> = ${MINUS}(${Array(e).fill(b).join('·')}).` } : null, { value: R(-b * e), note: 'An exponent means repeated multiplication, not base × exponent.' }].filter(Boolean),
        check: { kind: 'num', js: `-(${b}**${e})` },
      };
    },
  });

  topic({
    id: 'evaluate', unit: 'u1', name: 'Evaluate expressions (substitution)', prereqs: ['exp_neg'],
    idea: 'Substitution: replace each variable with its value, in parentheses. Then follow the order of operations: parentheses, exponents, multiply/divide, add/subtract. The parentheses keep a negative value under its exponent: if x = −3, then x² = (−3)² = 9.',
    gen(H) {
      const shape = H.pick(['quad', 'quad', 'two', 'geo']);
      if (shape === 'two') {
        const x = H.nz(-5, 5), y = H.nz(-5, 5), a = H.nz(-4, 5), b = H.nz(-6, 6);
        const ans = a * x * x * y + b * y;
        const p = poly([[a, { x: 2, y: 1 }], [b, { y: 1 }]]);
        return {
          ask: `Evaluate when <i>x</i> = ${fmtN(x)} and <i>y</i> = ${fmtN(y)}.`, math: fmtP(p), answer: { type: 'num', value: R(ans) },
          steps: [`Substitute: ${a === 1 ? '' : a === -1 ? MINUS : fmtN(a)}(${fmtN(x)})<sup>2</sup>(${fmtN(y)}) ${b < 0 ? MINUS : '+'} ${Math.abs(b)}(${fmtN(y)})`, `(${fmtN(x)})<sup>2</sup> = ${x * x}`, `= ${fmtN(a * x * x * y)} ${b * y < 0 ? MINUS : '+'} ${Math.abs(b * y)} = ${fmtN(ans)}`],
          traps: x < 0 ? [{ value: R(-a * x * x * y + b * y), note: `Put the value in parentheses: (${fmtN(x)})² = ${x * x}, not ${fmtN(-x * x)}.` }] : [],
          check: { kind: 'num', js: `(${a})*(${x})**2*(${y})+(${b})*(${y})` },
        };
      }
      if (shape === 'geo') {
        const x = H.ri(2, 9), a = H.ri(1, 6), b = H.ri(1, Math.min(6, x - 1));
        const ans = (x + a) * (x - b);
        return {
          ask: `A rectangle has length (<i>x</i> + ${a}) and width (<i>x</i> ${MINUS} ${b}). Find its area when <i>x</i> = ${x}.`, math: '<i>A</i> = <i>lw</i>', answer: { type: 'num', value: R(ans) },
          steps: [`Substitute: <i>A</i> = (${x} + ${a})(${x} ${MINUS} ${b})`, `= (${x + a})(${x - b}) = ${ans}`],
          traps: [], check: { kind: 'num', js: `(${x}+${a})*(${x}-${b})` },
        };
      }
      const x = H.nz(-5, 5), a = H.nz(-4, 5), b = H.nz(-7, 7), c = H.nz(-9, 9);
      const ans = a * x * x + b * x + c;
      const p = poly([[a, X2], [b, X], [c, {}]]);
      const traps = [];
      if (x < 0) traps.push({ value: R(-a * x * x + b * x + c), note: `Put the value in parentheses: (${fmtN(x)})² = ${x * x}, not ${fmtN(-x * x)}.` });
      traps.push({ value: R(a * a * x * x + b * x + c), note: `Square <i>x</i> first, then multiply by ${a}. The exponent applies only to <i>x</i>.` });
      return {
        ask: `Evaluate when <i>x</i> = ${fmtN(x)}.`, math: fmtP(p), answer: { type: 'num', value: R(ans) },
        steps: [`Substitute: ${a === 1 ? '' : a === -1 ? MINUS : fmtN(a)}(${fmtN(x)})<sup>2</sup> ${b < 0 ? MINUS : '+'} ${Math.abs(b) === 1 ? '' : Math.abs(b)}(${fmtN(x)}) ${c < 0 ? MINUS : '+'} ${Math.abs(c)}`, `Exponent first: (${fmtN(x)})<sup>2</sup> = ${x * x}`, `= ${fmtN(a * x * x)} ${b * x < 0 ? MINUS : '+'} ${Math.abs(b * x)} ${c < 0 ? MINUS : '+'} ${Math.abs(c)} = ${fmtN(ans)}`],
        traps, check: { kind: 'num', js: `(${a})*(${x})**2+(${b})*(${x})+(${c})` },
      };
    },
  });

  topic({
    id: 'distribute', unit: 'u1', name: 'Distributive property', prereqs: ['int_ops'],
    idea: 'a(b + c) = ab + ac. The number outside multiplies every term inside, sign included. A negative sign in front of parentheses means multiplying by −1: it changes the sign of every term inside, so −(x − 5) = −x + 5.',
    gen(H) {
      const negOnly = H.coin(0.3);
      const a = negOnly ? -1 : H.pick([2, 3, 4, 5, 6, 7, 8, -2, -3, -4, -5, -6]);
      const b = H.nz(-6, 6), c = H.nz(-9, 9);
      const inner = lin(b, c), ans = P.scale(inner, a);
      const traps = [{ value: poly([[a * b, X], [c, {}]]), note: 'Multiply the outside number by every term inside the parentheses, not just the first.' }];
      if (a < 0) traps.push({ value: poly([[a * b, X], [-a * c, {}]]), note: 'The negative multiplies every term: −(x − 5) = −x + 5, and −2(x + 3) = −2x − 6.' });
      return {
        ask: 'Simplify using the distributive property.', math: (a === -1 ? MINUS : fmtN(a)) + fmtFactor(inner),
        answer: { type: 'expr', value: ans, std: true },
        steps: [`Multiply ${fmtN(a)} by each term: ${par(a)}(${fmtTerms([P.terms(inner)[0]])}) ${MINUS === '' ? '' : '+'} ${par(a)}(${fmtN(c)})`, `= ${fmtP(ans)}`],
        traps, check: { kind: 'poly', js: `(${a})*((${b})*x+(${c}))` },
      };
    },
  });

  topic({
    id: 'combine_like', unit: 'u1', name: 'Combine like terms', prereqs: ['int_ops'],
    idea: 'Like terms have exactly the same variables with the same exponents: 3x² and −5x² are alike, 3x² and 3x are not. Add the coefficients of like terms and keep the variable part the same. Write the result in standard form (highest degree first).',
    gen(H) {
      const list = H.shuffle([[H.nz(-7, 7), X2], [H.nz(-7, 7), X2], [H.nz(-9, 9), X], [H.nz(-9, 9), X], [H.nz(-12, 12), {}]]);
      const ans = poly(list);
      if (Object.keys(ans).length < 2) return null;
      const shown = list.map(([c, m]) => ({ c: R(c), m }));
      const traps = [];
      return {
        ask: 'Simplify. Write your answer in standard form.', math: fmtTerms(shown), answer: { type: 'expr', value: ans, std: true },
        steps: ['Group like terms: ' + ['x2', 'x', ''].map(k => shown.filter(t => monoKey(t.m) === (k === 'x2' ? 'x^2' : k)).map(t => fmtTerms([t])).join(', ')).filter(Boolean).map(s => '[' + s + ']').join(' '), `Add the coefficients: ${fmtP(ans)}`],
        traps, check: { kind: 'poly', js: list.map(([c, m]) => `(${c})` + (m.x ? `*x**${m.x}` : '')).join('+') },
      };
    },
  });
  // combine_like may return null: wrap with retry
  (function () { const g = byId.combine_like.gen; byId.combine_like.gen = H => H.retry(() => g(H)); })();

  topic({
    id: 'exp_product', unit: 'u2', name: 'Multiplying monomials', prereqs: ['exp_neg'],
    idea: 'Multiply the coefficients, then multiply the variables by adding exponents of the same base: x² · x³ = x⁵, because that is (x·x)(x·x·x). Example: (3x²y)(−4xy³) = −12x³y⁴.',
    gen(H) {
      const two = H.coin(0.4);
      const a = H.nz(-6, 7), b = H.nz(-6, 7);
      const m1 = two ? { x: H.ri(1, 4), y: H.ri(1, 3) } : { x: H.ri(1, 5) };
      const m2 = two ? { x: H.ri(1, 3), y: H.ri(1, 3) } : { x: H.ri(1, 5) };
      const t1 = P.term(a, m1), t2 = P.term(b, m2), ans = P.mul(t1, t2);
      const wrongM = {}; for (const v of new Set([...Object.keys(m1), ...Object.keys(m2)])) wrongM[v] = (m1[v] || 1) * (m2[v] || 1);
      const traps = [];
      if (monoKey(wrongM) !== Object.keys(ans)[0]) traps.push({ value: P.term(a * b, wrongM), note: 'When multiplying, add the exponents: x² · x³ = x⁵, not x⁶.' });
      return {
        ask: 'Multiply.', math: fmtFactor(t1) + fmtFactor(t2), answer: { type: 'expr', value: ans, std: true },
        steps: [`Coefficients: ${par(a)} · ${par(b)} = ${fmtN(a * b)}`, 'Variables: add exponents of the same base', `= ${fmtP(ans)}`],
        traps, check: { kind: 'poly', js: `(${jsP(t1)})*(${jsP(t2)})` },
      };
    },
  });

  topic({
    id: 'eq_solve', unit: 'u1', name: 'Solve linear equations', prereqs: ['distribute', 'combine_like'],
    idea: 'Simplify each side first (distribute, combine like terms). Then get the variable terms on one side and the numbers on the other by doing the same thing to both sides. Divide by the coefficient last. Check by substituting back in.',
    gen(H) {
      return H.retry(() => {
        const s = H.nz(-9, 12), a = H.pick([2, 3, 4, 5, -2, -3]), b = H.nz(-8, 8), c = H.nz(-6, 6);
        if (a === c) return null;
        const d = a * (s + b) - c * s;
        const L = P.scale(lin(1, b), a), Rr = lin(c, d);
        return {
          ask: 'Solve for <i>x</i>.', math: `${fmtN(a)}${fmtFactor(lin(1, b))} = ${fmtP(Rr)}`, answer: { type: 'num', value: R(s) },
          steps: [`Distribute: ${fmtP(L)} = ${fmtP(Rr)}`, `${c < 0 ? 'Add' : 'Subtract'} ${fmtTerms([{ c: R(Math.abs(c)), m: X }])} ${c < 0 ? 'to' : 'from'} both sides: ${fmtP(lin(a - c, a * b))} = ${fmtN(d)}`, `${a * b < 0 ? 'Add' : 'Subtract'} ${Math.abs(a * b)} ${a * b < 0 ? 'to' : 'from'} both sides: ${fmtTerms([{ c: R(a - c), m: X }])} = ${fmtN(d - a * b)}`, `Divide by ${fmtN(a - c)}: <i>x</i> = ${fmtN(s)}`],
          traps: [{ value: R(d - b), note: 'Distribute to both terms inside the parentheses before solving.' }].filter(t => !req(t.value, R(s))),
          check: { kind: 'eq', js: `(${a})*(x+(${b}))===(${c})*x+(${d})` },
        };
      });
    },
  });

  /* ----- Polynomials ----- */
  const VARS2 = [['x', 'y'], ['a', 'b'], ['m', 'n']];
  topic({
    id: 'poly_degree', unit: 'u2', name: 'Degree of a polynomial', prereqs: ['exp_product'],
    idea: 'The degree of a term is the sum of the exponents of its variables: 7a²b² has degree 2 + 2 = 4, and 5x has degree 1. A number by itself has degree 0. The degree of a polynomial is the highest degree of any of its terms.',
    gen(H) {
      const [u, v] = H.pick(VARS2);
      const mk = () => H.pick([{ [u]: H.ri(1, 4) }, { [u]: H.ri(1, 3), [v]: H.ri(1, 3) }, { [v]: H.ri(1, 4) }]);
      if (H.coin(0.4)) {
        const m = { [u]: H.ri(1, 4), [v]: H.ri(1, 4) };
        const t = P.term(H.nz(-9, 9), m);
        return {
          ask: 'What is the degree of this term?', math: fmtP(t), answer: { type: 'num', value: R(monoDeg(m)) },
          steps: [`Add the exponents: ${m[u]} + ${m[v]} = ${monoDeg(m)}`],
          traps: [{ value: R(Math.max(m[u], m[v])), note: 'Add the exponents of all the variables in a term: 7a²b² has degree 2 + 2 = 4.' }].filter(t2 => t2.value.n !== monoDeg(m)),
          check: { kind: 'degree', poly: t, value: monoDeg(m) },
        };
      }
      return H.retry(() => {
        const ms = [mk(), mk(), {}];
        const keys = ms.map(monoKey); if (new Set(keys).size < 3) return null;
        const terms = H.shuffle(ms.map(m => ({ c: R(H.nz(-9, 9)), m })));
        const p = poly(terms.map(t => [t.c, t.m]));
        const deg = P.degree(p);
        const maxSingle = Math.max(...ms.map(m => Math.max(0, ...Object.values(m))));
        return {
          ask: 'What is the degree of this polynomial?', math: fmtTerms(terms), answer: { type: 'num', value: R(deg) },
          steps: ['Degree of each term: ' + terms.map(t => `${fmtTerms([t])} → ${monoDeg(t.m)}`).join(', '), `The highest is ${deg}.`],
          traps: maxSingle !== deg ? [{ value: R(maxSingle), note: 'For a term with more than one variable, add the exponents. The polynomial’s degree is the largest term degree.' }] : [],
          check: { kind: 'degree', poly: p, value: deg },
        };
      });
    },
  });

  topic({
    id: 'std_form', unit: 'u2', name: 'Standard form', prereqs: ['poly_degree'],
    idea: 'Standard form puts terms in descending total degree, ending with the constant. For a term with several variables, add their exponents: x², xy, and y² all have degree 2. Terms with the same degree can appear in any order. Our worked examples display x², then xy, then y² for consistency.',
    gen(H) {
      return H.retry(() => {
        const multi = H.coin(0.5);
        let ms;
        if (multi) ms = H.shuffle([{ x: 2 }, { x: 1, y: 1 }, { y: 2 }, { x: H.pick([1, 3]) }, { y: 1 }, {}]).slice(0, 4);
        else ms = H.shuffle([{ x: 4 }, { x: 3 }, { x: 2 }, { x: 1 }, {}]).slice(0, 4);
        const terms = ms.map(m => ({ c: R(H.nz(-9, 9)), m }));
        const correct = terms.slice().sort((s, t) => cmpMono(s.m, t.m));
        const shownOrder = H.shuffle(terms);
        if (standardCheck(toText(fmtTerms(shownOrder))).ok) return null;
        const asc = correct.slice().reverse();
        const byCoef = terms.slice().sort((s, t) => rval(t.c) - rval(s.c));
        const alphaFirst = terms.slice().sort((s, t) => (monoLex(s.m) || '~') < (monoLex(t.m) || '~') ? -1 : 1);
        // A degree tie is valid in either order, so it cannot be a distractor.
        const candidates = [
          {html:fmtTerms(asc),note:'That is lowest degree first. Start with the highest total degree.'},
          {html:fmtTerms(alphaFirst),note:'Order by total degree first; variable names do not override degree.'},
          {html:fmtTerms(byCoef),note:'Order by total degree, not by the size of the coefficient.'},
          {html:fmtTerms(shownOrder),note:'Move higher-degree terms before lower-degree terms.'},
          {html:fmtTerms(correct.slice(1).concat(correct[0])),note:'The highest-degree term belongs before lower-degree terms.'},
        ];
        const ds = [...new Map(candidates.filter(c=>!standardCheck(toText(c.html)).ok).map(c=>[c.html,c])).values()];
        if (ds.length < 2) return null;
        return {
          ask: 'Which shows this polynomial in standard form?', math: fmtTerms(shownOrder),
          answer: mc(H, { html: fmtTerms(correct) }, ds),
          steps: ['Degrees: ' + correct.map(t => `${fmtTerms([t])} → ${monoDeg(t.m)}`).join(', '), multi ? 'Terms with the same total degree can appear in any order. We display x², then xy, then y².' : 'Highest degree first.', `Standard form: ${fmtTerms(correct)}`],
          traps: [], check: { kind: 'stdmc', correct },
        };
      });
    },
  });

  function randQuad(H, multi) {
    if (multi) return poly([[H.nz(-8, 8), { x: 2 }], [H.nz(-9, 9), { x: 1, y: 1 }], [H.nz(-8, 8), { y: 2 }]]);
    return poly([[H.nz(-9, 9), X2], [H.nz(-12, 12), X], [H.nz(-15, 15), {}]]);
  }
  topic({
    id: 'poly_add', unit: 'u2', name: 'Adding polynomials', prereqs: ['combine_like', 'std_form'],
    idea: 'To add polynomials, drop the parentheses and combine like terms. Then write the answer in standard form.',
    gen(H) {
      return H.retry(() => {
        const multi = H.coin(0.3);
        const p = randQuad(H, multi), q = randQuad(H, multi), ans = P.add(p, q);
        if (Object.keys(ans).length < 2) return null;
        return {
          ask: 'Add. Write your answer in standard form.', math: `${fmtFactor(p)} + ${fmtFactor(q)}`, answer: { type: 'expr', value: ans, std: true },
          steps: [`Drop the parentheses: ${joinP(p, q)}`, 'Combine like terms (same variables, same exponents).', `= ${fmtP(ans)}`],
          traps: [], check: { kind: 'poly', js: `(${jsP(p)})+(${jsP(q)})` },
        };
      });
    },
  });

  topic({
    id: 'poly_sub', unit: 'u2', name: 'Subtracting polynomials', prereqs: ['poly_add', 'distribute'],
    idea: 'To subtract, distribute the negative into the parentheses that follow it. Multiplying by −1 changes the sign of every term inside. Then add as usual: combine like terms and write the answer in standard form.',
    gen(H) {
      return H.retry(() => {
        const multi = H.coin(0.25);
        const p = randQuad(H, multi), q = randQuad(H, multi), ans = P.sub(p, q);
        if (Object.keys(ans).length < 2) return null;
        const qt = P.terms(q);
        const trap = P.add(p, P.add(P.neg(poly([[qt[0].c, qt[0].m]])), poly(qt.slice(1).map(t => [t.c, t.m]))));
        return {
          ask: 'Subtract. Write your answer in standard form.', math: `${fmtFactor(p)} ${MINUS} ${fmtFactor(q)}`, answer: { type: 'expr', value: ans, std: true },
          steps: [`Distribute the negative (multiply by ${MINUS}1): ${MINUS}${fmtFactor(q)} = ${fmtP(P.neg(q))}`, `Now add: ${joinP(p, P.neg(q))}`, `Combine like terms: ${fmtP(ans)}`],
          traps: [{ value: trap, note: 'The negative goes to EVERY term in the second polynomial, not just the first one.' }, { value: P.add(p, q), note: 'You added. Subtracting means changing the sign of every term in the second polynomial first.' }],
          check: { kind: 'poly', js: `(${jsP(p)})-(${jsP(q)})` },
        };
      });
    },
  });

  /* ----- Multiplying ----- */
  topic({
    id: 'mono_poly', unit: 'u2', name: 'Monomial times polynomial', prereqs: ['distribute', 'exp_product'],
    idea: 'Distribute the monomial to every term of the polynomial. Multiply coefficients and add exponents: 2x(3x² − 4x + 5) = 6x³ − 8x² + 10x.',
    gen(H) {
      const a = H.pick([2, 3, 4, 5, -2, -3, -4]), m = H.ri(1, 2);
      const mono = P.term(a, { x: m });
      const inner = poly([[H.nz(-6, 6), X2], [H.nz(-8, 8), X], [H.nz(-9, 9), {}]]);
      const ans = P.mul(mono, inner);
      const it = P.terms(inner);
      const trap = P.add(P.mul(mono, poly([[it[0].c, it[0].m]])), poly(it.slice(1).map(t => [t.c, t.m])));
      return {
        ask: 'Multiply. Write your answer in standard form.', math: `${fmtP(mono)}${fmtFactor(inner)}`, answer: { type: 'expr', value: ans, std: true },
        steps: ['Multiply ' + fmtP(mono) + ' by each term: ' + it.map(t => `${fmtP(mono)} · ${par0(t)} = ${fmtP(P.mul(mono, poly([[t.c, t.m]])))}`).join('; '), `= ${fmtP(ans)}`],
        traps: [{ value: trap, note: 'Multiply the monomial by every term inside the parentheses.' }],
        check: { kind: 'poly', js: `(${jsP(mono)})*(${jsP(inner)})` },
      };
    },
  });
  function par0(t) { const s = fmtTerms([t]); return t.c.n < 0 ? '(' + s + ')' : s; }

  function binomials(H, opts) {
    opts = opts || {};
    const a = opts.lead1 ? 1 : H.pick([1, 1, 1, 2, 3]);
    const c = opts.lead1 ? 1 : H.pick([1, 1, 1, 2]);
    const b = H.nz(opts.pos ? 1 : -9, 9), d = H.nz(opts.pos ? 1 : -9, 9);
    return { a, b, c, d, f1: lin(a, b), f2: lin(c, d) };
  }
  topic({
    id: 'binom_tiles', unit: 'u2', name: 'Multiplying with algebra tiles', prereqs: ['mono_poly'],
    idea: 'With algebra tiles, one factor goes across the top and the other down the side. Fill the rectangle: a big blue square is x², a green bar is x, and a small yellow square is 1. Count the tiles in the rectangle to read off the product.',
    gen(H) {
      const a = H.pick([1, 1, 2]), b = H.ri(1, 4), c = H.pick([1, 1, 2]), d = H.ri(1, 4);
      const f1 = lin(a, b), f2 = lin(c, d), ans = P.mul(f1, f2);
      return {
        ask: `The tiles model ${fmtFactor(f1)}${fmtFactor(f2)}. What polynomial do the tiles in the rectangle show? Write it in standard form.`,
        math: tilesSVG(a, b, c, d), answer: { type: 'expr', value: ans, std: true },
        steps: [`Blue squares (<i>x</i><sup>2</sup>): ${a * c}`, `Green bars (<i>x</i>): ${a * d} + ${b * c} = ${a * d + b * c}`, `Yellow squares (1): ${b * d}`, `= ${fmtP(ans)}`],
        traps: [{ value: poly([[a * c, X2], [b * d, {}]]), note: 'Count the green x bars too: they come from the outer and inner products.' }],
        check: { kind: 'poly', js: `(${a}*x+${b})*(${c}*x+${d})` },
      };
    },
  });

  topic({
    id: 'binom_box', unit: 'u2', name: 'Multiplying with the box (area) model', prereqs: ['mono_poly', 'combine_like'],
    idea: 'Write one binomial across the top of a 2×2 box and the other down the side. Each box is the product of its row and column. Then add the four boxes, combine the two like (x) terms, and write the result in standard form.',
    gen(H) {
      const { a, b, c, d, f1, f2 } = binomials(H);
      const cols = [P.term(a, X), P.c(b)], rows = [P.term(c, X), P.c(d)];
      const cells = [P.mul(rows[0], cols[0]), P.mul(rows[0], cols[1]), P.mul(rows[1], cols[0]), P.mul(rows[1], cols[1])];
      const ans = P.mul(f1, f2);
      return {
        ask: 'Fill in the box, then write the product in standard form.', math: `${fmtFactor(f1)}${fmtFactor(f2)}`,
        answer: { type: 'box', cols, rows, cells, value: ans, std: true },
        steps: cells.map((cell, k) => `Box ${k + 1}: (${fmtP(rows[Math.floor(k / 2)])})(${fmtP(cols[k % 2])}) = ${fmtP(cell)}`).concat([`Add all four cells: ${cells.map(fmtP).join(' + ').replace(/\+ −/g, MINUS + ' ')}`, `Combine like terms: ${fmtP(ans)}`]),
        traps: [{ value: poly([[a * c, X2], [b * d, {}]]), note: 'Add all four boxes. The two x-terms combine into the middle term.' }],
        check: { kind: 'poly', js: `(${a}*x+(${b}))*(${c}*x+(${d}))` },
      };
    },
  });

  topic({
    id: 'binom_dist', unit: 'u2', name: 'Multiplying with distribution', prereqs: ['binom_box'],
    idea: 'Standard algorithm: break apart the first factor and distribute each piece to the second factor. (x + 10)(x + 7) = x(x + 7) + 10(x + 7) = x² + 7x + 10x + 70 = x² + 17x + 70. Then combine like terms and write it in standard form.',
    gen(H) {
      let f1, f2, a, b, c, d, two = H.coin(0.2);
      if (two) {
        a = 1; c = H.pick([1, 2]); b = H.nz(-6, 6); d = H.nz(-6, 6);
        f1 = poly([[a, { x: 1 }], [b, { y: 1 }]]); f2 = poly([[c, { x: 1 }], [d, { y: 1 }]]);
      } else ({ a, b, c, d, f1, f2 } = binomials(H));
      const ans = P.mul(f1, f2);
      const t1 = P.terms(f1);
      const parts = t1.map(t => P.mul(poly([[t.c, t.m]]), f2));
      const trap = poly([[a * c, two ? { x: 2 } : X2], [b * d, two ? { y: 2 } : {}]]);
      return {
        ask: 'Multiply. Write your answer in standard form.', math: `${fmtFactor(f1)}${fmtFactor(f2)}`, answer: { type: 'expr', value: ans, std: true },
        steps: [`Break apart the first factor: ${fmtTerms([t1[0]])}${fmtFactor(f2)} ${t1[1].c.n < 0 ? MINUS : '+'} ${fmtTerms([{ c: R(Math.abs(t1[1].c.n), t1[1].c.d), m: t1[1].m }])}${fmtFactor(f2)}`, `Distribute: ${joinP(parts[0], parts[1])}`, `Combine like terms: ${fmtP(ans)}`],
        traps: [{ value: trap, note: 'Every term in the first factor multiplies every term in the second. You should get 4 products before combining.' }],
        check: { kind: 'poly', js: `(${jsP(f1)})*(${jsP(f2)})` },
      };
    },
  });

  topic({
    id: 'special_sq', unit: 'u2', name: 'Square of a sum or difference', prereqs: ['binom_dist'],
    idea: '(a + b)² = a² + 2ab + b² and (a − b)² = a² − 2ab + b². (x + 5)² means (x + 5)(x + 5), so don’t forget the middle term: (x + 5)² = x² + 10x + 25, not x² + 25.',
    gen(H) {
      const a = H.pick([1, 1, 1, 2, 3]), b = H.ri(1, 9) * (H.coin() ? 1 : -1);
      const two = H.coin(0.15);
      const f = two ? poly([[a, { x: 1 }], [b, { y: 1 }]]) : lin(a, b);
      const ans = P.mul(f, f);
      const A = two ? fmtTerms([{ c: R(a), m: X }]) : fmtTerms([{ c: R(a), m: X }]);
      const B = two ? fmtTerms([{ c: R(Math.abs(b)), m: { y: 1 } }]) : String(Math.abs(b));
      return {
        ask: 'Multiply. Write your answer in standard form.', math: `${fmtFactor(f)}<sup>2</sup>`, answer: { type: 'expr', value: ans, std: true },
        steps: [`Pattern: (<i>a</i> ${b < 0 ? MINUS : '+'} <i>b</i>)<sup>2</sup> = <i>a</i><sup>2</sup> ${b < 0 ? MINUS : '+'} 2<i>ab</i> + <i>b</i><sup>2</sup> with <i>a</i> = ${A}, <i>b</i> = ${B}`, `<i>a</i><sup>2</sup> = ${fmtP(P.mul(P.term(a, X), P.term(a, X)))}, 2<i>ab</i> = ${fmtP(P.scale(P.mul(P.term(a, X), two ? P.term(Math.abs(b), { y: 1 }) : P.c(Math.abs(b))), 2))}, <i>b</i><sup>2</sup> = ${fmtP(two ? P.term(b * b, { y: 2 }) : P.c(b * b))}`, `= ${fmtP(ans)}`],
        traps: [{ value: two ? poly([[a * a, { x: 2 }], [b * b, { y: 2 }]]) : poly([[a * a, X2], [b * b, {}]]), note: '(a + b)² is not a² + b². Write it as (a + b)(a + b): the middle term 2ab comes from the two inside products.' },
          { value: two ? poly([[a * a, { x: 2 }], [2 * a * b, { x: 1, y: 1 }], [-b * b, { y: 2 }]]) : poly([[a * a, X2], [2 * a * b, X], [-b * b, {}]]), note: 'b² is always positive: (−5)² = 25.' }],
        check: { kind: 'poly', js: `(${jsP(f)})**2` },
      };
    },
  });

  topic({
    id: 'special_diff', unit: 'u2', name: 'Product of a sum and a difference', prereqs: ['binom_dist'],
    idea: '(a + b)(a − b) = a² − b². The middle terms cancel: (x + 6)(x − 6) = x² − 6x + 6x − 36 = x² − 36.',
    gen(H) {
      const a = H.pick([1, 1, 2, 3, 4, 5]), b = H.ri(1, 10);
      const f1 = lin(a, b), f2 = lin(a, -b);
      const first = H.coin() ? [f1, f2] : [f2, f1];
      const ans = P.mul(f1, f2);
      return {
        ask: 'Multiply. Write your answer in standard form.', math: `${fmtFactor(first[0])}${fmtFactor(first[1])}`, answer: { type: 'expr', value: ans, std: true },
        steps: [`Pattern: (<i>a</i> + <i>b</i>)(<i>a</i> ${MINUS} <i>b</i>) = <i>a</i><sup>2</sup> ${MINUS} <i>b</i><sup>2</sup>`, `The middle terms cancel: ${fmtTerms([{ c: R(-a * b), m: X }])} + ${fmtTerms([{ c: R(a * b), m: X }])} = 0`, `= ${fmtP(ans)}`],
        traps: [{ value: poly([[a * a, X2], [b * b, {}]]), note: 'The last term is (+b)(−b) = −b², so it is negative.' }],
        check: { kind: 'poly', js: `(${jsP(f1)})*(${jsP(f2)})` },
      };
    },
  });

  topic({
    id: 'poly_mult', unit: 'u2', name: 'Binomial times trinomial', prereqs: ['binom_dist', 'std_form'],
    idea: 'Same idea as two binomials: distribute each term of the first factor to every term of the second. (x + 2)(x² − 3x + 4) = x(x² − 3x + 4) + 2(x² − 3x + 4). You get 6 products; combine like terms.',
    gen(H) {
      const b = H.nz(-6, 6);
      const f1 = lin(1, b), f2 = poly([[H.pick([1, 1, 2]), X2], [H.nz(-6, 6), X], [H.nz(-9, 9), {}]]);
      const ans = P.mul(f1, f2);
      return {
        ask: 'Multiply. Write your answer in standard form.', math: `${fmtFactor(f1)}${fmtFactor(f2)}`, answer: { type: 'expr', value: ans, std: true },
        steps: [`<i>x</i>${fmtFactor(f2)} = ${fmtP(P.mul(P.v('x'), f2))}`, `${fmtN(b)}${fmtFactor(f2)} = ${fmtP(P.scale(f2, b))}`, `Combine like terms: ${fmtP(ans)}`],
        traps: [], check: { kind: 'poly', js: `(${jsP(f1)})*(${jsP(f2)})` },
      };
    },
  });

  /* ----- Geometry application ----- */
  const UNITS_LEN = ['feet', 'inches', 'meters', 'centimeters'];
  const SING = { feet: 'foot', inches: 'inch', meters: 'meter', centimeters: 'centimeter' };
  const un = (n, u) => n === 1 ? SING[u] : u;
  topic({
    id: 'geo_expr', unit: 'u2', name: 'Polynomials for area and perimeter', prereqs: ['binom_dist', 'evaluate'],
    idea: 'Write the formula first (A = lw, A = s², P = 2l + 2w, A = ½bh). Then substitute the side lengths, in parentheses, and simplify into standard form.',
    gen(H) {
      const shape = H.pick(['rectA', 'rectA', 'sqA', 'rectP', 'triA', 'shaded']);
      if (shape === 'rectA' || shape === 'rectP') {
        const { f1, f2 } = binomials(H, { pos: false });
        const L = P.terms(f1)[1].c.n < 0 && P.terms(f2)[1].c.n < 0 ? P.add(f1, P.c(10)) : f1;
        const ans = shape === 'rectA' ? P.mul(L, f2) : P.add(P.scale(L, 2), P.scale(f2, 2));
        if (shape === 'rectP' && P.isConst(ans)) return byId.geo_expr.gen(H);
        return {
          ask: `Write a polynomial in standard form for the ${shape === 'rectA' ? 'area' : 'perimeter'} of the rectangle.`,
          math: rectSVG(svgText(fmtP(L)), svgText(fmtP(f2))), answer: { type: 'expr', value: ans, std: true },
          steps: shape === 'rectA' ? ['Formula: <i>A</i> = <i>lw</i>', `Substitute: <i>A</i> = ${fmtFactor(L)}${fmtFactor(f2)}`, `Multiply: <i>A</i> = ${fmtP(ans)}`] : ['Formula: <i>P</i> = 2<i>l</i> + 2<i>w</i>', `Substitute: <i>P</i> = 2${fmtFactor(L)} + 2${fmtFactor(f2)}`, `Simplify: <i>P</i> = ${fmtP(ans)}`],
          traps: shape === 'rectA' ? [{ value: P.add(P.scale(L, 2), P.scale(f2, 2)), note: 'That is the perimeter. Area is length times width.' }] : [{ value: P.add(L, f2), note: 'Perimeter counts all four sides: 2l + 2w.' }, { value: P.mul(L, f2), note: 'That is the area. Perimeter adds the sides: 2l + 2w.' }],
          check: { kind: 'poly', js: shape === 'rectA' ? `(${jsP(L)})*(${jsP(f2)})` : `2*(${jsP(L)})+2*(${jsP(f2)})` },
        };
      }
      if (shape === 'sqA') {
        const a = H.pick([1, 2, 3]), b = H.nz(-7, 9); const s = lin(a, b); const ans = P.mul(s, s);
        return {
          ask: 'Write a polynomial in standard form for the area of the square.', math: rectSVG(svgText(fmtP(s)), svgText(fmtP(s)), { square: true }),
          answer: { type: 'expr', value: ans, std: true },
          steps: ['Formula: <i>A</i> = <i>s</i><sup>2</sup>', `Substitute: <i>A</i> = ${fmtFactor(s)}<sup>2</sup>`, `Square of a ${b < 0 ? 'difference' : 'sum'}: <i>A</i> = ${fmtP(ans)}`],
          traps: [{ value: poly([[a * a, X2], [b * b, {}]]), note: '(a + b)² has a middle term: 2ab.' }],
          check: { kind: 'poly', js: `(${jsP(s)})**2` },
        };
      }
      if (shape === 'triA') {
        const k = H.ri(1, 6), base = lin(2, 2 * k), h = lin(1, H.ri(1, 9));
        const ans = P.scale(P.mul(base, h), R(1, 2));
        return {
          ask: 'Write a polynomial in standard form for the area of the triangle.', math: triSVG(svgText(fmtP(base)), svgText(fmtP(h))),
          answer: { type: 'expr', value: ans, std: true },
          steps: ['Formula: <i>A</i> = ' + frac(1, 2) + '<i>bh</i>', `Substitute: <i>A</i> = ${frac(1, 2)}${fmtFactor(base)}${fmtFactor(h)}`, `${frac(1, 2)}${fmtFactor(base)} = ${fmtP(P.scale(base, R(1, 2)))}, so <i>A</i> = ${fmtFactor(P.scale(base, R(1, 2)))}${fmtFactor(h)} = ${fmtP(ans)}`],
          traps: [{ value: P.mul(base, h), note: 'Triangle area is half of base times height.' }],
          check: { kind: 'poly', js: `0.5*(${jsP(base)})*(${jsP(h)})` },
        };
      }
      // shaded: big square side (x + a) with a small square of side b cut out
      const a = H.ri(2, 8), b = H.ri(1, 5); const s = lin(1, a); const ans = P.sub(P.mul(s, s), P.c(b * b));
      return {
        ask: `A square with side (<i>x</i> + ${a}) has a small square of side ${b} cut out of its corner. Write a polynomial in standard form for the area that is left.`,
        math: rectSVG(svgText(fmtP(s)), svgText(fmtP(s)), { square: true, cut: String(b) }), answer: { type: 'expr', value: ans, std: true },
        steps: ['Area left = big area − cut-out area', `= ${fmtFactor(s)}<sup>2</sup> ${MINUS} ${b}<sup>2</sup>`, `= ${fmtP(P.mul(s, s))} ${MINUS} ${b * b} = ${fmtP(ans)}`],
        traps: [{ value: P.sub(poly([[1, X2], [a * a, {}]]), P.c(b * b)), note: '(x + a)² has a middle term 2ax.' }],
        check: { kind: 'poly', js: `(x+${a})**2-${b * b}` },
      };
    },
  });

  topic({
    id: 'geo_shaded', unit: 'u2', name: 'Shaded area with polynomials', prereqs: ['geo_expr', 'poly_sub'],
    idea: 'Shaded area = the whole figure − the unshaded part. Write each area as a polynomial (A = lw or A = s²), then subtract the whole unshaded area. Keep it in parentheses so the minus sign reaches every term: −(x² + 5x + 6) = −x² − 5x − 6.',
    pitfalls: ['Subtract the entire unshaded area, every term, not just the first one.', 'A frame adds its width to both ends, so each outer side is twice the frame width longer.'],
    gen(H) {
      const kind = H.pick(['rect', 'rect', 'square', 'frame', 'squareHole']);
      let oL, oW, iL, iW, setup = [], ask, fig;
      if (kind === 'rect') {
        const a = H.ri(1, 9), b = H.ri(3, 9), c = H.ri(1, a), d = H.coin(0.6) ? H.ri(1, b - 1) : -H.ri(1, 4);
        oL = lin(2, a); oW = lin(1, b); iL = lin(1, c); iW = lin(1, d);
        ask = 'The large rectangle has a smaller rectangle cut out of it. Write a polynomial in standard form for the area of the shaded region.';
        fig = shadedSVG(svgText(fmtP(oL)), svgText(fmtP(oW)), svgText(fmtP(iL)), svgText(fmtP(iW)));
      } else if (kind === 'square') {
        const a = H.ri(1, 9), b = H.ri(1, 6);
        oL = oW = lin(1, a); iL = iW = lin(1, -b);
        ask = `A square with side (<i>x</i> + ${a}) has a square with side (<i>x</i> ${MINUS} ${b}) cut out of it. Write a polynomial in standard form for the area of the shaded region.`;
        fig = shadedSVG(svgText(fmtP(oL)), svgText(fmtP(oW)), svgText(fmtP(iL)), svgText(fmtP(iW)), { square: true });
      } else if (kind === 'frame') {
        const p = H.ri(1, 8), k = H.ri(1, 4), u = H.pick(UNITS_LEN);
        iL = lin(1, p); iW = P.v('x'); oL = lin(1, p + 2 * k); oW = lin(1, 2 * k);
        ask = `A photo is <i>x</i> ${u} wide and (<i>x</i> + ${p}) ${u} long. It has a frame ${k} ${un(k, u)} wide on every side. Write a polynomial in standard form for the area of the frame (the shaded region).`;
        fig = shadedSVG(svgText(fmtP(oL)), svgText(fmtP(oW)), svgText(fmtP(iL)), 'x', { frame: true });
        setup = [`The frame adds ${k} to both ends of each side. Outer length: <i>x</i> + ${p} + 2(${k}) = ${fmtP(oL)}. Outer width: <i>x</i> + 2(${k}) = ${fmtP(oW)}`];
      } else {
        const a = H.ri(1, 9), b = H.ri(1, 9), m = H.pick([2, 3]);
        oL = lin(m, a); oW = lin(2, b); iL = iW = P.v('x');
        ask = 'A square with side <i>x</i> is cut out of the rectangle. Write a polynomial in standard form for the area of the shaded region.';
        fig = shadedSVG(svgText(fmtP(oL)), svgText(fmtP(oW)), 'x', 'x', { squareHole: true });
      }
      const outer = P.mul(oL, oW), inner = P.mul(iL, iW);
      // Every unshaded area here leads with x², so the rest is what a one-term subtraction forgets.
      const ans = P.sub(outer, inner), innerRest = P.sub(inner, poly([[1, X2]]));
      const traps = [
        { value: P.add(outer, inner), note: 'The cut-out is not part of the shaded region. Subtract its area; don’t add it.' },
        { value: outer, note: 'That is the area of the whole outer shape. Subtract the unshaded part.' },
      ];
      if (Object.keys(innerRest).length) traps.push({ value: P.add(ans, P.scale(innerRest, 2)), note: `Subtract every term of the inner area: ${MINUS}(${fmtP(inner)}) = ${fmtP(P.scale(inner, -1))}.` });
      return {
        ask, math: fig, answer: { type: 'expr', value: ans, std: true },
        steps: [...setup, 'Shaded area = outer area − unshaded area', `Outer: ${fmtFactor(oL)}${fmtFactor(oW)} = ${fmtP(outer)}`, `Unshaded: ${fmtFactor(iL)}${fmtFactor(iW)} = ${fmtP(inner)}`, `Subtract, keeping the unshaded area in parentheses: (${fmtP(outer)}) ${MINUS} (${fmtP(inner)})`, `= ${fmtP(ans)}`],
        traps: traps.filter(t => !P.eq(t.value, ans)),
        check: { kind: 'poly', js: `(${jsP(oL)})*(${jsP(oW)})-(${jsP(iL)})*(${jsP(iW)})` },
      };
    },
  });

  topic({
    id: 'geo_solve', unit: 'u2', name: 'Polynomial word problems', prereqs: ['geo_expr', 'eq_solve', 'special_sq'],
    idea: 'Steps: (1) Write the formula(s). (2) Draw the shapes and label the sides with expressions. (3) Substitute the expressions into the formulas and set up the equation the problem describes. (4) Multiply, simplify (the x² terms usually cancel), and solve. Give the answer with units outside the parentheses, like (4) feet.',
    gen(H) {
      const u = H.pick(UNITS_LEN);
      const kind = H.pick(['equal', 'grow', 'reshape', 'differ']);
      if (kind === 'equal') {
        return H.retry(() => {
          const a = H.ri(2, 12), b = H.ri(1, 9); if (a <= b) return null;
          const x = (a * b) / (a - b); if (!Number.isInteger(x) || x <= b || x > 60) return null;
          return {
            ask: `A square has an unknown side length of <i>x</i>. A rectangle has a length that is ${a} ${un(a, u)} longer than the square’s side and a width that is ${b} ${un(b, u)} shorter. The areas of the square and the rectangle are equal. Find the side length of the square.`,
            math: rectSVG('x', 'x', { square: true }) + rectSVG('x + ' + a, 'x − ' + b), answer: { type: 'num', value: R(x), units: u },
            steps: ['Formulas: <i>A</i> = <i>s</i><sup>2</sup> and <i>A</i> = <i>lw</i>', `Diagram: square side <i>x</i>; rectangle <i>l</i> = <i>x</i> + ${a}, <i>w</i> = <i>x</i> ${MINUS} ${b}`, `Areas are equal: (<i>x</i> + ${a})(<i>x</i> ${MINUS} ${b}) = <i>x</i><sup>2</sup>`, `Multiply: <i>x</i><sup>2</sup> + ${a - b}<i>x</i> ${MINUS} ${a * b} = <i>x</i><sup>2</sup>`, `Subtract <i>x</i><sup>2</sup>: ${a - b}<i>x</i> ${MINUS} ${a * b} = 0, so ${a - b}<i>x</i> = ${a * b}`, `<i>x</i> = ${x}. Answer: (${x}) ${u}`],
            traps: [], check: { kind: 'word', js: `(x+${a})*(x-${b})===x*x && x-${b}>0` },
          };
        });
      }
      if (kind === 'grow') {
        const x = H.ri(3, 20), k = H.ri(1, 6); const A = 2 * k * x + k * k;
        return {
          ask: `Each side of a square is increased by ${k} ${un(k, u)}. The area of the new square is ${A} square ${u} more than the area of the original square. What was the side length of the original square?`,
          math: rectSVG('x', 'x', { square: true }) + rectSVG('x + ' + k, 'x + ' + k, { square: true }), answer: { type: 'num', value: R(x), units: u },
          steps: ['Formula: <i>A</i> = <i>s</i><sup>2</sup>. Let the original side be <i>x</i>.', `New side: <i>x</i> + ${k}`, `Equation: (<i>x</i> + ${k})<sup>2</sup> = <i>x</i><sup>2</sup> + ${A}`, `Square of a sum: <i>x</i><sup>2</sup> + ${2 * k}<i>x</i> + ${k * k} = <i>x</i><sup>2</sup> + ${A}`, `Subtract <i>x</i><sup>2</sup>: ${2 * k}<i>x</i> + ${k * k} = ${A}, so ${2 * k}<i>x</i> = ${A - k * k}`, `<i>x</i> = ${x}. Answer: (${x}) ${u}`],
          traps: [{ value: R(A - k * k, k), note: '(x + k)² = x² + 2kx + k², so the x-term is 2kx, not kx.' }].filter(t => !req(t.value, R(x))),
          check: { kind: 'word', js: `(x+${k})**2===x*x+${A}` },
        };
      }
      if (kind === 'reshape') {
        return H.retry(() => {
          const m = H.ri(1, 8), p = H.ri(2, 9), q = H.ri(1, 6); if (p <= q) return null;
          const w = q * (m + p) / (p - q); if (!Number.isInteger(w) || w <= q || w > 60) return null;
          return {
            ask: `A rectangle’s length is ${m} ${un(m, u)} more than its width. If the length is increased by ${p} ${un(p, u)} and the width is decreased by ${q} ${un(q, u)}, the area stays the same. Find the original width.`,
            math: rectSVG('w + ' + m, 'w') + rectSVG('w + ' + (m + p), 'w − ' + q), answer: { type: 'num', value: R(w), units: u },
            steps: [`Let the width be <i>w</i>. Then the length is <i>w</i> + ${m}. Formula: <i>A</i> = <i>lw</i>`, `New rectangle: length <i>w</i> + ${m + p}, width <i>w</i> ${MINUS} ${q}`, `Equal areas: (<i>w</i> + ${m + p})(<i>w</i> ${MINUS} ${q}) = <i>w</i>(<i>w</i> + ${m})`, `Multiply: <i>w</i><sup>2</sup> + ${m + p - q}<i>w</i> ${MINUS} ${q * (m + p)} = <i>w</i><sup>2</sup> + ${m}<i>w</i>`, `Subtract <i>w</i><sup>2</sup> and ${m}<i>w</i>: ${p - q}<i>w</i> = ${q * (m + p)}`, `<i>w</i> = ${w}. Answer: (${w}) ${u}`],
            traps: [], check: { kind: 'word', js: `(x+${m + p})*(x-${q})===x*(x+${m}) && x-${q}>0` },
          };
        });
      }
      return H.retry(() => {
        const x = H.ri(2, 20), a = H.ri(1, 8), b = H.ri(1, 8); const D = (a + b) * x + a * b;
        return {
          ask: `A square has side length <i>x</i> ${u}. A rectangle is ${a} ${un(a, u)} longer and ${b} ${un(b, u)} wider than the square. The rectangle’s area is ${D} square ${u} more than the square’s area. Find <i>x</i>.`,
          math: rectSVG('x', 'x', { square: true }) + rectSVG('x + ' + a, 'x + ' + b), answer: { type: 'num', value: R(x), units: u },
          steps: ['Formulas: <i>A</i> = <i>s</i><sup>2</sup> and <i>A</i> = <i>lw</i>', `Rectangle: (<i>x</i> + ${a})(<i>x</i> + ${b})`, `Equation: (<i>x</i> + ${a})(<i>x</i> + ${b}) = <i>x</i><sup>2</sup> + ${D}`, `Multiply: <i>x</i><sup>2</sup> + ${a + b}<i>x</i> + ${a * b} = <i>x</i><sup>2</sup> + ${D}`, `Subtract <i>x</i><sup>2</sup> and ${a * b}: ${a + b}<i>x</i> = ${D - a * b}`, `<i>x</i> = ${x}. Answer: (${x}) ${u}`],
          traps: [], check: { kind: 'word', js: `(x+${a})*(x+${b})===x*x+${D}` },
        };
      });
    },
  });

  /* ---------------- numbers with roots, π and decimals ---------------- */
  // Floating-point evaluator for answers like 2.5, 3/4, -2+sqrt(5), 2√3, 8^(2/3).
  function evalNum(src) {
    const s = String(src).toLowerCase().replace(/[−–—]/g, '-').replace(/[×·•]/g, '*').replace(/÷/g, '/')
      .replace(/²/g, '^2').replace(/³/g, '^3').replace(/√/g, 'sqrt').replace(/π/g, 'pi').replace(/\s+/g, '');
    let i = 0;
    const peek = () => s[i];
    function expr() { let v = term(); while (peek() === '+' || peek() === '-') { const op = s[i++]; const r = term(); v = op === '+' ? v + r : v - r; } return v; }
    function term() {
      let v = unary();
      for (;;) {
        const c = peek();
        if (c === '*') { i++; v *= unary(); }
        else if (c === '/') { i++; v /= unary(); }
        else if (c && (/[0-9.(]/.test(c) || s.startsWith('sqrt', i) || s.startsWith('pi', i))) v *= power();
        else break;
      }
      return v;
    }
    function unary() { if (peek() === '-') { i++; return -unary(); } if (peek() === '+') { i++; return unary(); } return power(); }
    function power() { const b = atom(); if (peek() === '^') { i++; const e = unary(); return Math.pow(b, e); } return b; }
    function atom() {
      if (s.startsWith('sqrt', i)) { i += 4; const a = s[i] === '(' ? atom() : number(); if (a < 0) throw new ParseError('negative root'); return Math.sqrt(a); }
      if (s.startsWith('pi', i)) { i += 2; return Math.PI; }
      if (peek() === '(') { i++; const v = expr(); if (s[i++] !== ')') throw new ParseError('missing )'); return v; }
      return number();
    }
    function number() { const m = /^(\d+\.?\d*|\.\d+)/.exec(s.slice(i)); if (!m) throw new ParseError('number expected'); i += m[0].length; return parseFloat(m[0]); }
    if (!s) throw new ParseError('empty');
    const v = expr();
    if (i !== s.length || !isFinite(v)) throw new ParseError('could not read');
    return v;
  }
  const stripUnits = src => String(src).toLowerCase()
    .replace(/^[a-z]\s*=\s*/, '')
    .replace(/\b(square|sq\.?|cubic)\b/g, '')
    .replace(/\b(feet|foot|ft|inches|inch|in|meters|meter|m|centimeters|centimeter|cm|yards|yard|yd|miles|mile|mi|units|unit|degrees|degree|seconds|second|sec|s|minutes|minute|min|hours|hour|hrs|hr|days|day|weeks|week|years|year|yrs|dollars|dollar|cents|cent|liters|liter|l|gallons|gallon|gal|ounces|ounce|oz|pounds|pound|lbs|lb|grams|gram|g|people|students|tickets|bacteria|cells)\b\.?/g, '')
    .replace(/[$,%]/g, '').trim();

  /* ---------------- exact radicals ----------------
   * A radical value is {k: R} keyed by a square-free radicand (1 = rational part). */
  function sqrtSplit(n) { if (!Number.isSafeInteger(n) || n < 0 || n > 10000000) throw new ParseError('use a smaller radicand'); let out = 1, inn = n; for (let f = 2; f * f <= inn; f++) while (inn % (f * f) === 0) { out *= f; inn /= f * f; } return [out, inn]; }
  const RAD = {
    clean(m) { const o = {}; for (const k in m) if (m[k].n !== 0) o[k] = m[k]; return o; },
    rat(r) { return RAD.clean({ 1: typeof r === 'number' ? R(r) : r }); },
    root(n, coef) { // coef·√n for a non-negative integer n
      if (n === 0) return {};
      const [o, i] = sqrtSplit(n); return RAD.clean({ [i]: rmul(coef || R(1), R(o)) });
    },
    add(a, b) { const o = Object.assign({}, a); for (const k in b) o[k] = o[k] ? radd(o[k], b[k]) : b[k]; return RAD.clean(o); },
    scale(a, r) { const o = {}; for (const k in a) o[k] = rmul(a[k], r); return RAD.clean(o); },
    mul(a, b) { let o = {}; for (const ka in a) for (const kb in b) o = RAD.add(o, RAD.root(+ka * +kb, rmul(a[ka], b[kb]))); return o; },
    eq(a, b) { const ka = Object.keys(a), kb = Object.keys(b); return ka.length === kb.length && ka.every(k => b[k] && req(a[k], b[k])); },
    val(a) { let v = 0; for (const k in a) v += rval(a[k]) * Math.sqrt(+k); return v; },
  };
  // Parse an exact radical expression; reports whether it was written in simplest form.
  function parseRadical(src) {
    const s = normalize(src).replace(/√/g, 'r').replace(/sqrt/g, 'r').replace(/\s+/g, '');
    let i = 0, simplified = true, terms = 0;
    const peek = () => s[i];
    function expr(top) {
      let v = term(); if (top) terms++;
      while (peek() === '+' || peek() === '-') { const op = s[i++]; const r = term(); if (top) terms++; v = RAD.add(v, op === '+' ? r : RAD.scale(r, R(-1))); }
      return v;
    }
    function term() {
      let v = unary();
      for (;;) {
        const c = peek();
        if (c === '*') { i++; const r = unary(); if (Object.keys(v).some(k => k !== '1') && Object.keys(r).some(k => k !== '1')) simplified = false; v = RAD.mul(v, r); }
        else if (c === '/') {
          i++; const d = unary(); const ks = Object.keys(d);
          if (ks.length !== 1) throw new ParseError('divide by a single term');
          if (ks[0] !== '1') simplified = false; // radical left in a denominator
          const k = +ks[0];
          v = RAD.mul(v, RAD.root(k, rdiv(R(1), rmul(d[ks[0]], R(k)))));
        }
        else if (c && /[0-9(r]/.test(c)) { const r = power(); if (Object.keys(v).some(k => k !== '1') && Object.keys(r).some(k => k !== '1')) simplified = false; v = RAD.mul(v, r); }
        else break;
      }
      return v;
    }
    function unary() { if (peek() === '-') { i++; return RAD.scale(unary(), R(-1)); } if (peek() === '+') { i++; return unary(); } return power(); }
    function power() {
      const b = atom();
      if (peek() === '^') { i++; const m = /^\d+/.exec(s.slice(i)); if (!m) throw new ParseError('bad exponent'); i += m[0].length; if (+m[0] > 40) throw new ParseError('exponent too large'); if (+m[0] !== 1) simplified = false; let v = RAD.rat(1); for (let k = 0; k < +m[0]; k++) v = RAD.mul(v, b); return v; }
      return b;
    }
    function atom() {
      if (peek() === 'r') {
        i++;
        let inner;
        if (peek() === '(') { i++; inner = expr(false); if (s[i++] !== ')') throw new ParseError('missing )'); }
        else { const m = /^\d+/.exec(s.slice(i)); if (!m) throw new ParseError('number after the root sign'); i += m[0].length; inner = RAD.rat(+m[0]); }
        const ks = Object.keys(inner);
        if (ks.length > 1 || (ks.length === 1 && ks[0] !== '1')) throw new ParseError('nested roots');
        const r = ks.length ? inner['1'] : R(0);
        if (r.n < 0) throw new ParseError('negative under a root');
        if (r.d !== 1) simplified = false;
        if (sqrtSplit(r.n)[0] !== 1 || r.n === 1 || r.n === 0) simplified = false;
        return RAD.root(r.n * r.d, R(1, r.d));
      }
      if (peek() === '(') { i++; const v = expr(false); if (s[i++] !== ')') throw new ParseError('missing )'); return v; }
      const m = /^(\d+\.?\d*|\.\d+)/.exec(s.slice(i)); if (!m) throw new ParseError('number expected'); i += m[0].length;
      const txt = m[0];
      if (txt.includes('.')) { const [w, f] = txt.split('.'); const d = Math.pow(10, f.length); return RAD.rat(R(parseInt(w || '0', 10) * d + parseInt(f || '0', 10), d)); }
      return RAD.rat(R(parseInt(txt, 10)));
    }
    if (!s) throw new ParseError('empty');
    const v = expr(true);
    if (i !== s.length) throw new ParseError('could not read');
    if (terms > Math.max(1, Object.keys(v).length)) simplified = false;
    return { value: v, simplified };
  }
  const ROOT = (k) => `√<span class="rad">${k}</span>`;
  function fmtRad(m) {
    const ks = Object.keys(m).sort((a, b) => +a - +b);
    if (!ks.length) return '0';
    return ks.map((k, idx) => {
      const c = m[k], neg = c.n < 0, an = Math.abs(c.n);
      let body;
      if (k === '1') body = c.d === 1 ? String(an) : frac(an, c.d);
      else { const top = (an === 1 ? '' : an) + ROOT(k); body = c.d === 1 ? top : frac(top, c.d); }
      return idx === 0 ? (neg ? MINUS : '') + body : (neg ? ' ' + MINUS + ' ' : ' + ') + body;
    }).join('');
  }
  function typeRad(m) {
    const ks = Object.keys(m).sort((a, b) => +a - +b);
    if (!ks.length) return '0';
    return ks.map((k, idx) => {
      const c = m[k], neg = c.n < 0, an = Math.abs(c.n);
      let body = k === '1' ? String(an) : (an === 1 ? '' : an) + 'sqrt(' + k + ')';
      if (c.d !== 1) body = (k !== '1' && an === 1 ? '1' : '') + body + '/' + c.d;
      return idx === 0 ? (neg ? '-' : '') + body : (neg ? ' - ' : ' + ') + body;
    }).join('');
  }

  /* ---------------- inequalities (one variable, unions with "or") ---------------- */
  const NONE_RE = /^(no solutions?|no real solutions?|none|∅|\{\s*\})$/;
  const ALL_RE = /^(all real numbers|all reals|all numbers|infinitely many( solutions)?|\(-?∞,\s*∞\)|\(-inf,\s*inf\))$/;
  function ivKey(iv) { return [iv.lo ? iv.lo.n + '/' + iv.lo.d : '-inf', iv.loInc ? 1 : 0, iv.hi ? iv.hi.n + '/' + iv.hi.d : 'inf', iv.hiInc ? 1 : 0].join('|'); }
  function ivSort(list) { return list.slice().sort((a, b) => (a.lo ? rval(a.lo) : -Infinity) - (b.lo ? rval(b.lo) : -Infinity)); }
  function parseIneq(src, v) {
    v = v || 'x';
    let s = String(src).toLowerCase().trim().replace(/[−–—]/g, '-').replace(/≤|=<|⩽/g, '<=').replace(/≥|=>|⩾/g, '>=');
    if (NONE_RE.test(s)) return [];
    if (ALL_RE.test(s)) return [{ lo: null, loInc: false, hi: null, hiInc: false }];
    const parts = s.split(/\s*(?:\bor\b|∪|\bunion\b)\s*/);
    return canonicalIntervals(parts.flatMap(part => {
      const and = part.split(/\s*\band\b\s*/);
      if (and.length > 1) return and.map(piece => parseIneq(piece, v)).reduce(intersectIntervals);
      const interval = /^([([])\s*([^,]+),\s*([^,]+)\s*([)\]])$/.exec(part.trim());
      if (interval) {
        const bound = txt => /^(?:[+-]?inf(?:inity)?|[+-]?∞)$/.test(txt.trim()) ? null : parseNumber(txt);
        const lo = bound(interval[2]), hi = bound(interval[3]);
        if ((!lo && !/^-(inf(?:inity)?|∞)$/.test(interval[2].trim())) || (!hi && !/^\+?(inf(?:inity)?|∞)$/.test(interval[3].trim()))) throw new ParseError('infinite endpoint');
        if ((!lo && interval[1] === '[') || (!hi && interval[4] === ']')) throw new ParseError('infinite endpoint is open');
        return { lo, hi, loInc: interval[1] === '[', hiInc: interval[4] === ']' };
      }
      const pieces = part.replace(/\s+/g, '').split(/(<=|>=|<|>)/);
      const side = txt => { if (txt === v) return 'v'; const p = parseTokens(tokenize(normalize(txt))); if (!P.isConst(p)) throw new ParseError('compare the variable to a number'); return P.constVal(p); };
      if (pieces.length === 3) {
        const [A, op, B] = pieces; const a = side(A), b = side(B);
        if (a === 'v' && b !== 'v') return rayFor(op, b);
        if (b === 'v' && a !== 'v') return rayFor(flipOp(op), a);
        throw new ParseError('write it like x < 3');
      }
      if (pieces.length === 5) {
        const [A, o1, B, o2, C] = pieces; const a = side(A), b = side(B), c = side(C);
        if (b !== 'v' || a === 'v' || c === 'v') throw new ParseError('write it like -2 < x <= 5');
        if (o1[0] === '<' && o2[0] === '<') return { lo: a, loInc: o1 === '<=', hi: c, hiInc: o2 === '<=' };
        if (o1[0] === '>' && o2[0] === '>') return { lo: c, loInc: o2 === '>=', hi: a, hiInc: o1 === '>=' };
        throw new ParseError('both signs must point the same way');
      }
      throw new ParseError('could not read the inequality');
    }));
  }
  function flipOp(op) { return { '<': '>', '>': '<', '<=': '>=', '>=': '<=' }[op]; }
  function rayFor(op, k) {
    if (op === '<') return { lo: null, loInc: false, hi: k, hiInc: false };
    if (op === '<=') return { lo: null, loInc: false, hi: k, hiInc: true };
    if (op === '>') return { lo: k, loInc: false, hi: null, hiInc: false };
    return { lo: k, loInc: true, hi: null, hiInc: false };
  }
  function canonicalIntervals(list) {
    const sorted = ivSort(list).filter(iv => iv.lo === null || iv.hi === null || rval(iv.lo) < rval(iv.hi) || req(iv.lo, iv.hi) && iv.loInc && iv.hiInc);
    const out = [];
    for (const entry of sorted) {
      const iv = {...entry, loInc: entry.lo !== null && entry.loInc, hiInc: entry.hi !== null && entry.hiInc};
      const prev = out[out.length - 1];
      if (!prev) { out.push(iv); continue; }
      const join = prev.hi === null || iv.lo === null || rval(iv.lo) < rval(prev.hi) || req(iv.lo, prev.hi) && (iv.loInc || prev.hiInc);
      if (!join) { out.push(iv); continue; }
      if (prev.lo !== null && iv.lo !== null && req(prev.lo, iv.lo)) prev.loInc ||= iv.loInc;
      if (prev.hi !== null && (iv.hi === null || rval(iv.hi) > rval(prev.hi))) { prev.hi = iv.hi; prev.hiInc = iv.hiInc; }
      else if (prev.hi !== null && iv.hi !== null && req(prev.hi, iv.hi)) prev.hiInc ||= iv.hiInc;
    }
    return out;
  }
  function intersectIntervals(a, b) {
    const out = [];
    for (const x of a) for (const y of b) {
      const L = x.lo === null ? y : y.lo === null ? x : rval(x.lo) > rval(y.lo) ? x : y;
      const H = x.hi === null ? y : y.hi === null ? x : rval(x.hi) < rval(y.hi) ? x : y;
      out.push({lo:L.lo, hi:H.hi,
        loInc:L.loInc && (!(x.lo && y.lo && req(x.lo,y.lo)) || x.loInc && y.loInc),
        hiInc:H.hiInc && (!(x.hi && y.hi && req(x.hi,y.hi)) || x.hiInc && y.hiInc)});
    }
    return canonicalIntervals(out);
  }
  const ineqEq = (a, b) => { const A = canonicalIntervals(a), B = canonicalIntervals(b); return A.length === B.length && A.every((iv, k) => ivKey(iv) === ivKey(B[k])); };

  function ineqContains(list, x) {
    return list.some(iv => (iv.lo === null || (iv.loInc ? x >= rval(iv.lo) - 1e-12 : x > rval(iv.lo) + 1e-12)) && (iv.hi === null || (iv.hiInc ? x <= rval(iv.hi) + 1e-12 : x < rval(iv.hi) - 1e-12)));
  }
  function fmtIneq(list, v, plain) {
    v = v || 'x';
    const V = plain ? v : `<i>${v}</i>`, F = plain ? (r => r.d === 1 ? String(r.n) : r.n + '/' + r.d) : fmtR;
    const LE = plain ? '<=' : '≤', GE = plain ? '>=' : '≥';
    if (!list.length) return 'no solution';
    return list.map(iv => {
      if (iv.lo === null && iv.hi === null) return 'all real numbers';
      if (iv.lo === null) return `${V} ${iv.hiInc ? LE : '<'} ${F(iv.hi)}`;
      if (iv.hi === null) return `${V} ${iv.loInc ? GE : '>'} ${F(iv.lo)}`;
      return `${F(iv.lo)} ${iv.loInc ? LE : '<'} ${V} ${iv.hiInc ? LE : '<'} ${F(iv.hi)}`;
    }).join(' or ');
  }
  function numberLineSVG(list) {
    const bounds = []; list.forEach(iv => { if (iv.lo) bounds.push(rval(iv.lo)); if (iv.hi) bounds.push(rval(iv.hi)); });
    const mid = bounds.length ? Math.round((Math.min(...bounds) + Math.max(...bounds)) / 2) : 0;
    const span = Math.max(5, Math.ceil((bounds.length ? Math.max(...bounds) - Math.min(...bounds) : 0) / 2) + 2);
    const lo = mid - span, hi = mid + span, W = 340, pad = 20, X = x => pad + (x - lo) / (hi - lo) * (W - 2 * pad);
    let out = `<svg class="nline" viewBox="0 0 ${W} 60" role="img" aria-label="Number line"><line x1="6" y1="28" x2="${W - 6}" y2="28" class="g-axis"/>`;
    out += `<path d="M6 28 l8 -5 v10 z M${W - 6} 28 l-8 -5 v10 z" class="g-arrow"/>`;
    for (let t = lo; t <= hi; t++) out += `<line x1="${X(t)}" y1="23" x2="${X(t)}" y2="33" class="g-axis"/>` + ((hi - lo) <= 14 || t % 2 === 0 ? `<text x="${X(t)}" y="50" class="g-lbl" text-anchor="middle">${t < 0 ? MINUS + -t : t}</text>` : '');
    for (const iv of list) {
      const a = iv.lo ? X(rval(iv.lo)) : 8, b = iv.hi ? X(rval(iv.hi)) : W - 8;
      out += `<line x1="${a}" y1="28" x2="${b}" y2="28" class="g-ray"/>`;
      if (!iv.lo) out += `<path d="M4 28 l10 -6 v12 z" class="g-ray-head"/>`;
      if (!iv.hi) out += `<path d="M${W - 4} 28 l-10 -6 v12 z" class="g-ray-head"/>`;
      if (iv.lo) out += `<circle cx="${a}" cy="28" r="5.5" class="${iv.loInc ? 'g-dot' : 'g-open'}"/>`;
      if (iv.hi) out += `<circle cx="${b}" cy="28" r="5.5" class="${iv.hiInc ? 'g-dot' : 'g-open'}"/>`;
    }
    return out + '</svg>';
  }

  /* ---------------- ordered pairs and solution sets ---------------- */
  function parsePair(src) {
    let s = String(src).toLowerCase().replace(/[−–—]/g, '-').trim();
    if (s.startsWith('(') && s.endsWith(')')) s = s.slice(1, -1).trim();
    const named = /^([xy])\s*=\s*([^,;]+?)(?:\s*[,;]\s*|\s+and\s+)([xy])\s*=\s*([^,;]+)$/.exec(s);
    if (named) {
      if (named[1] === named[3]) throw new ParseError('give x and y once');
      const a = parseNumber(named[2]), b = parseNumber(named[4]);
      return named[1] === 'x' ? [a, b] : [b, a];
    }
    const m = /^([^,]+),([^,]+)$/.exec(s);
    if (!m) throw new ParseError('write it like (2, -3)');
    return [parseNumber(m[1]), parseNumber(m[2])];
  }
  function parseSet(src) {
    const s = String(src).toLowerCase().replace(/[−–—]/g, '-').trim().replace(/^\{\s*(.*?)\s*\}$/, '$1');
    if (NONE_RE.test(s) || s === '') return [];
    const out = [];
    for (let part of s.split(/\s*(?:,|;|\band\b|\bor\b)\s*/)) {
      part = part.replace(/^[a-z]\s*=\s*/, '').trim();
      if (!part) continue;
      const pm = part.split(/±|\+-|\+\/-/);
      if (pm.length === 2) { out.push(evalNum(part.replace(/±|\+-|\+\/-/, '+')), evalNum(part.replace(/±|\+-|\+\/-/, '-'))); }
      else out.push(evalNum(stripUnits(part)));
    }
    if (!out.length) throw new ParseError('empty');
    return out;
  }
  const setNum = v => (typeof v === 'number' ? v : rval(v));
  function setEq(got, want, tol) {
    const uniq = arr => arr.filter((x, k) => arr.findIndex(y => Math.abs(y - x) <= tol) === k);
    const G = uniq(got), W = uniq(want.map(setNum));
    if (G.length !== W.length) return false;
    return W.every(w => G.some(x => Math.abs(x - w) <= tol));
  }
  function fmtSetVal(v, digits = 2) { return typeof v === 'number' ? (Math.round(v * 10 ** digits) / 10 ** digits).toString().replace('-', MINUS) : fmtR(v); }

  /* ---------------- factored form ---------------- */
  function intContent(p) { let g = 0; for (const k in p) { const c = p[k].c; if (c.d !== 1) return 1; g = gcd(g, c.n); } return g || 1; }
  function commonVar(p) { const ks = Object.keys(p); if (ks.length < 2) return null; const vs = Object.keys(p[ks[0]].m); return vs.find(v => ks.every(k => (p[k].m[v] || 0) > 0)) || null; }
  const isSquare = n => n >= 0 && Math.round(Math.sqrt(n)) ** 2 === n;
  function stillFactorable(p) {
    if (Object.keys(p).length < 2) return false;
    if (Math.abs(intContent(p)) > 1) return true;
    if (commonVar(p)) return true;
    const vars = P.vars(p), deg = P.degree(p);
    const coef = m => { const k = monoKey(m); return p[k] ? p[k].c : R(0); };
    if (vars.length === 1 && deg === 2) { const v = vars[0]; const a = coef({ [v]: 2 }), b = coef({ [v]: 1 }), c = coef({}); const D = rsub(rmul(b, b), rmul(R(4), rmul(a, c))); return isSquare(D.n) && isSquare(D.d); }
    if (vars.length === 2 && deg === 2) { const [u, w] = vars; const a = coef({ [u]: 2 }), b = coef({ [u]: 1, [w]: 1 }), c = coef({ [w]: 2 }); if (Object.keys(p).length !== [a, b, c].filter(r => r.n).length) return false; const D = rsub(rmul(b, b), rmul(R(4), rmul(a, c))); return isSquare(D.n) && isSquare(D.d); }
    if (vars.length === 1 && deg >= 3) { // a rational root means a linear factor is left
      const v = vars[0]; const c0 = coef({}); const lead = coef({ [v]: deg });
      if (c0.n === 0) return true;
      const divisors = n => { n = Math.abs(n); if (n > 1000000) throw new ParseError('use smaller factors'); const out = []; for (let d = 1; d * d <= n; d++) if (n % d === 0) { out.push(d); if (d * d !== n) out.push(n / d); } return out; };
      for (const q of divisors(lead.n)) for (const r of divisors(c0.n)) for (const sgn of [1, -1]) if (Math.abs(P.evalAt(p, { [v]: sgn * r / q })) < 1e-9) return true;
    }
    return false;
  }
  function factoredCheck(src) {
    let toks = tokenize(normalize(src));
    while (toks[0]?.t === '(' && toks[toks.length - 1]?.t === ')') {
      let d = 0, enclosing = true;
      for (let k = 0; k < toks.length - 1; k++) { if (toks[k].t === '(') d++; if (toks[k].t === ')') d--; if (d === 0) { enclosing = false; break; } }
      if (!enclosing) break; toks = toks.slice(1, -1);
    }
    let depth = 0;
    for (let k = 0; k < toks.length; k++) {
      const t = toks[k], prev = toks[k - 1];
      if (t.t === '(') depth++; else if (t.t === ')') depth--;
      else if (depth === 0 && (t.t === '+' || t.t === '-') && prev && (prev.t === 'num' || prev.t === 'var' || prev.t === ')')) return { ok: false, reason: 'notFactored' };
    }
    const factors = []; let cur = [], k = 0;
    while (k < toks.length) {
      const t = toks[k];
      if (t.t === '(') {
        let d = 0, j = k; for (; j < toks.length; j++) { if (toks[j].t === '(') d++; if (toks[j].t === ')' && --d === 0) break; }
        let group = toks.slice(k, j + 1), exp = 1; k = j + 1;
        if (toks[k] && toks[k].t === '^' && toks[k + 1] && toks[k + 1].t === 'num') { exp = toks[k + 1].v.n; k += 2; }
        if (cur.length) { factors.push({ toks: cur, exp: 1 }); cur = []; }
        factors.push({ toks: group, exp });
        continue;
      }
      if (t.t === '*') { k++; continue; }
      cur.push(t); k++;
    }
    if (cur.length) factors.push({ toks: cur, exp: 1 });
    let nonMono = 0;
    for (const f of factors) {
      const base = f.toks.length === 1 && f.toks[0].t === '-' ? P.c(-1) : parseTokens(f.toks);
      if (Object.keys(base).length > 1) { nonMono++; if (stillFactorable(base)) return { ok: false, reason: 'notCompletely' }; }
    }
    if (!nonMono) return { ok: false, reason: 'notFactored' };
    return { ok: true };
  }
  function fmtFactors(fs) {
    const out = []; const used = fs.map(() => false);
    fs.forEach((f, k) => {
      if (used[k]) return;
      let n = 0; fs.forEach((g, j) => { if (!used[j] && P.eq(f, g)) { used[j] = true; n++; } });
      const mono = Object.keys(f).length <= 1;
      const body = mono ? (P.eq(f, P.c(-1)) ? MINUS : fmtP(f)) : fmtFactor(f);
      out.push(body + (n > 1 ? `<sup>${n}</sup>` : ''));
    });
    return out.join('');
  }
  function typeFactors(fs) { return fs.map(f => Object.keys(f).length <= 1 ? (P.eq(f, P.c(-1)) ? '-' : typeP(f)) : '(' + typeP(f) + ')').join(''); }

  /* ---------------- coordinate graphs and tables ---------------- */
  function graphSVG(o) {
    o = o || {};
    const xmin = o.xmin ?? -10, xmax = o.xmax ?? 10, ymin = o.ymin ?? -10, ymax = o.ymax ?? 10;
    const W = 300, H = 300, pad = 30;
    const X = x => pad + (x - xmin) / (xmax - xmin) * (W - 2 * pad), Y = y => H - pad - (y - ymin) / (ymax - ymin) * (H - 2 * pad);
    const nice = target => { const power=10**Math.floor(Math.log10(target)),ratio=target/power;return (ratio<=1?1:ratio<=2?2:ratio<=5?5:10)*power; };
    const xstep=o.step||Math.max(1,nice((xmax-xmin)/24)),ystep=o.step||Math.max(1,nice((ymax-ymin)/24));
    const xlabels=Math.max(o.labelStep||0,nice((xmax-xmin)/10)),ylabels=Math.max(o.labelStep||0,nice((ymax-ymin)/10));
    let s = `<svg class="graph" viewBox="0 0 ${W} ${H}" role="img" aria-label="${o.label || 'Graph'}">`;
    for (let x = Math.ceil(xmin / xstep) * xstep; x <= xmax; x += xstep) s += `<line x1="${X(x)}" y1="${Y(ymin)}" x2="${X(x)}" y2="${Y(ymax)}" class="g-grid"/>`;
    for (let y = Math.ceil(ymin / ystep) * ystep; y <= ymax; y += ystep) s += `<line x1="${X(xmin)}" y1="${Y(y)}" x2="${X(xmax)}" y2="${Y(y)}" class="g-grid"/>`;
    if (ymin <= 0 && ymax >= 0) s += `<line x1="${X(xmin)}" y1="${Y(0)}" x2="${X(xmax)}" y2="${Y(0)}" class="g-axis"/>`;
    if (xmin <= 0 && xmax >= 0) s += `<line x1="${X(0)}" y1="${Y(ymin)}" x2="${X(0)}" y2="${Y(ymax)}" class="g-axis"/>`;
    const ly = ymin <= 0 && ymax >= 0 ? Y(0) + 14 : H - pad + 16, lx = xmin <= 0 && xmax >= 0 ? X(0) - 4 : pad + 2;
    for (let x = Math.ceil(xmin / xlabels) * xlabels; x <= xmax; x += xlabels) if (x !== 0) s += `<text x="${X(x)}" y="${ly}" class="g-lbl g-small" text-anchor="middle">${fmtN(Number(x.toPrecision(12)))}</text>`;
    for (let y = Math.ceil(ymin / ylabels) * ylabels; y <= ymax; y += ylabels) if (y !== 0) s += `<text x="${lx}" y="${Y(y) + 4}" class="g-lbl g-small" text-anchor="end">${fmtN(Number(y.toPrecision(12)))}</text>`;
    // Clip a rectangular window by each linear half-plane. The intersection
    // stays bounded to the plot, and its boundary style is rendered separately.
    let region = [[xmin,ymin],[xmax,ymin],[xmax,ymax],[xmin,ymax]];
    for (const q of o.halfplanes || []) {
      const direction = q.op[0] === '>' ? -1 : 1;
      const value = p => direction * (q.a * p[0] + q.b * p[1] - q.c);
      const clipped = [];
      for (let i=0;i<region.length;i++) {
        const a=region[i], b=region[(i+1)%region.length], av=value(a), bv=value(b);
        if (av<=0) clipped.push(a);
        if ((av<=0)!==(bv<=0)) { const t=av/(av-bv); clipped.push([a[0]+t*(b[0]-a[0]),a[1]+t*(b[1]-a[1])]); }
      }
      region=clipped;
    }
    if (o.halfplanes?.length && region.length) s += `<polygon class="g-region" points="${region.map(p=>X(p[0])+','+Y(p[1])).join(' ')}" fill="#2563eb" fill-opacity="0.16"/>`;
    (o.curves || []).forEach((c, ci) => {
      let d = '', pen = false;
      const n = 240;
      for (let k = 0; k <= n; k++) {
        const x = xmin + (xmax - xmin) * k / n, y = c.f(x);
        if (!isFinite(y) || y < ymin - 1 || y > ymax + 1) { pen = false; continue; }
        d += (pen ? 'L' : 'M') + X(x).toFixed(1) + ' ' + Y(Math.max(ymin, Math.min(ymax, y))).toFixed(1); pen = true;
      }
      s += `<path d="${d}" class="g-curve${ci % 2 ? ' g-curve2' : ''}${c.dash ? ' g-dashed' : ''}"/>`;
    });
    (o.points || []).forEach(p => {
      const px = X(p[0]), py = Y(p[1]);
      s += `<circle cx="${px}" cy="${py}" r="4" class="${p[3] ? 'g-open' : 'g-dot'}"/>`;
      if (p[2]) {
        // Endpoint labels belong inside the SVG. Place a label left of a
        // right-edge point when its normal offset would clip the text.
        const labelWidth = String(p[2]).replace(/<[^>]*>/g, '').length * 11;
        const atRight = px + 6 + labelWidth > W - 4;
        const tx = Math.max(4, Math.min(W - 4, px + (atRight ? -6 : 6)));
        s += `<text x="${tx}" y="${py - 6}" class="g-lbl g-small"${atRight ? ' text-anchor="end"' : ''}>${p[2]}</text>`;
      }
    });
    return s + '</svg>';
  }
  function tableHTML(headers, rows) {
    return `<table class="vtable"><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
  }

  /* ---------------- generating + grading ---------------- */
  const PITFALLS = {
    int_ops: ['For addition, use sizes and signs; multiplication uses a different sign rule.', 'Subtracting a negative means adding its opposite.'],
    exp_neg: ['Check whether parentheses put the negative inside the exponent.', 'An exponent means repeated multiplication, not base times exponent.'],
    evaluate: ['Put negative substitution values in parentheses.', 'Apply exponents before multiplying by coefficients; substitute every variable.'],
    distribute: ['Multiply every term inside the parentheses.', 'Carry the outside sign into every product.'],
    combine_like: ['Only combine terms with identical variables and exponents.', 'Add coefficients; keep the variable part unchanged.'],
    exp_product: ['Multiply coefficients, then add exponents of the same base.', 'Keep different variables separate.'],
    eq_solve: ['Distribute before moving terms.', 'Apply each operation to both sides and substitute the solution back to check.'],
    poly_degree: ['Add all variable exponents within each term.', 'Take the highest term degree; coefficient size does not determine degree.'],
    std_form: ['Order by total degree first, highest first.', 'Keep each sign with its term; equal-degree terms can appear in any order.'],
    poly_add: ['Combine only like terms.', 'Adding does not change the signs inside the second polynomial.'],
    poly_sub: ['Change the sign of every term in the subtracted polynomial.', 'Then combine like terms; do not subtract only the first term.'],
    mono_poly: ['Multiply the monomial by every term.', 'Multiply coefficients and add exponents of matching variables.'],
    binom_tiles: ['Count the interior tiles, not the header tiles.', 'Include the x bars as well as the x-squared and unit tiles.'],
    binom_box: ['Each cell is its row term times its column term.', 'Include all four cells and combine the two middle terms with their signs.'],
    binom_dist: ['Every term in the first factor multiplies every term in the second.', 'Keep all four products before combining like terms.'],
    special_sq: ['A binomial square includes the middle term, twice the product.', 'The last square is positive even when the binomial is a difference.'],
    special_diff: ['The equal and opposite middle terms cancel.', 'The last product is negative; this pattern requires matching terms and opposite signs.'],
    poly_mult: ['A binomial times a trinomial gives six products.', 'Combine matching powers only after all products are present.'],
    geo_expr: ['Choose area or perimeter before substituting side lengths.', 'Area uses square units; perimeter uses length units. Triangle area includes one half.'],
    geo_solve: ['Distinguish an equal area from an increase in area.', 'Answer the requested original dimension, check all lengths are positive, and give length units.'],
  };
  const UNIT_ALIASES = { feet: ['feet', 'foot', 'ft'], inches: ['inches', 'inch', 'in'], meters: ['meters', 'meter', 'm'], centimeters: ['centimeters', 'centimeter', 'cm'],
    yards: ['yards', 'yard', 'yd'], miles: ['miles', 'mile', 'mi'], seconds: ['seconds', 'second', 'sec', 's'], minutes: ['minutes', 'minute', 'min'], hours: ['hours', 'hour', 'hr', 'hrs'],
    days: ['days', 'day'], years: ['years', 'year', 'yr', 'yrs'], dollars: ['dollars', 'dollar'], liters: ['liters', 'liter', 'l'], gallons: ['gallons', 'gallon', 'gal'], ounces: ['ounces', 'ounce', 'oz'], pounds: ['pounds', 'pound', 'lb', 'lbs'], 'fluid ounces': ['fluid ounces', 'fluid ounce', 'fl oz'], grams: ['grams', 'gram', 'g'], kilograms: ['kilograms', 'kilogram', 'kg'] };
  const LENGTH_UNITS = new Set(['feet', 'inches', 'meters', 'centimeters', 'yards', 'miles']);
  function unitsMatch(input, expected) {
    const s = normalize(input).trim().replace(/\.$/, '');
    if (expected === 'dollars' && /^\$/.test(s)) return true;
    if (/\b(square|sq|cubic)\b|\^\s*[23]\s*$/.test(s)) return false;
    return (UNIT_ALIASES[expected] || [expected]).some(u => new RegExp('(?:\\s|\\))' + u + '$').test(s));
  }
  function boxFeedback(a, correct) {
    return correct.map((ok, k) => ok ? '' : `Box ${k + 1}: ${fmtP(a.rows[Math.floor(k / 2)])} times ${fmtP(a.cols[k % 2])} gives ${fmtP(a.cells[k])}.`).filter(Boolean).join(' ');
  }
  function fallbackFeedback(problem, parsed) {
    const shownAs = problem.decimal || problem.ask !== undefined && decimalContext(problem) ? fmtDec : fmtR;
    const a = problem.answer;
    if ((a.type === 'expr' || a.type === 'box') && parsed) {
      const changes = [], matches = [];
      for (const key of new Set([...Object.keys(a.value), ...Object.keys(parsed)])) {
        const expected = a.value[key], actual = parsed[key];
        const m = (expected || actual).m;
        const label = monoDeg(m) ? fmtMono(m) + ' term' : 'constant term';
        if (expected && actual && req(expected.c, actual.c)) matches.push(label);
        else if (!expected) changes.push(`An extra ${label} appears in your answer; the correct result has no such term.`);
        else if (!actual) changes.push(`The ${label} is missing: include ${fmtTerms([expected])}.`);
        else changes.push(`The ${label} coefficient is ${fmtR(actual.c)}; it should be ${fmtR(expected.c)}.`);
      }
      return (matches.length ? `Your ${matches.join(' and ')} ${matches.length === 1 ? 'matches' : 'match'}. ` : '') + changes.slice(0, 3).join(' ') + ' Compare the products and signs in the worked steps below.';
    }
    if (a.type === 'num' && parsed) return `Your result ${shownAs(parsed)} differs from ${shownAs(a.value)}. Start with this step: ${problem.steps[0]} Then check each operation below.`;
    return 'Compare your work with each step below, then try a fresh problem. ' + problem.teaching.pitfalls[0];
  }
  function generate(topicId, rng, point) {
    const t = byId[topicId];
    if (!t) throw new Error('Unknown topic');
    const seed = Math.floor((rng || Math.random)() * 4294967296) >>> 0;
    return generateFromSeed(topicId, seed, point);
  }
  // `point` selects a knowledge point for topics that declare `points`; older topics ignore it,
  // so their saved seeds regenerate the same problems.
  function generateFromSeed(topicId, seed, point = 0) {
    const t = byId[topicId];
    if (!t || !Number.isInteger(seed) || seed < 0 || seed > 4294967295) throw new Error('Invalid problem seed');
    const H = helpers(mulberry32(seed));
    const pointIndex = t.points ? Math.max(0, Math.min(t.points.length - 1, Number.isInteger(point) ? point : 0)) : 0;
    const p = t.gen(H, pointIndex);
    p.topic = topicId;
    p.seed = seed;
    p.contentVersion = 'algebra-audit-1';
    p.answerHtml = answerHtml(p.answer, p);
    presentDecimals(p);
    // A trap identical to the answer would mislabel a right answer; drop it.
    p.traps = (p.traps || []).filter(t => t && t.value !== undefined && t.value !== null && !valuesEqual(p.answer, t.value, answerValue(p.answer)));
    const uniqueTraps = new Map();
    for (const trap of p.traps) {
      const key = encodeValue(p.answer, trap.value);
      const prior = uniqueTraps.get(key);
      if (prior) prior.note += ' Also check: ' + trap.note;
      else uniqueTraps.set(key, { value: trap.value, note: trap.note });
    }
    p.traps = [...uniqueTraps.values()];
    p.teaching = { idea: p.teachingIdea || t.idea, pitfalls: (p.pitfalls || PITFALLS[topicId] || t.pitfalls || []).slice() };
    return p;
  }
  function answerValue(a) { return a.type === 'pair' ? a.value : a.type === 'set' ? a.values : a.value; }
  function valuesEqual(a, x, y) {
    switch (a.type) {
      case 'num': return typeof x === 'number' || typeof y === 'number' ? Math.abs(setNum(x) - setNum(y)) <= (a.tol || 1e-9) : req(x, y);
      case 'expr': case 'box': case 'factored': return P.eq(x, y);
      case 'pair': return req(x[0], y[0]) && req(x[1], y[1]);
      case 'set': return setEq(x.map(setNum), y, a.tol || 1e-9);
      case 'ineq': return ineqEq(x, y);
      case 'radical': return RAD.eq(x, y);
      case 'complex': return req(x.re, y.re) && req(x.im, y.im);
      case 'ratexpr': return ratEq(x, y);
      case 'vector': return vecEq(x, y, a.parallel);
      case 'matrix': return matEq(x, y);
      case 'mc': return x === y;
    }
    return false;
  }
  // A set of y values (y-intercepts, "every real y", y coordinates) is written y = …, not x = ….
  function setVariable(problem) {
    const ask = problem ? toText(problem.ask ?? '') : '';
    return /\by[- ]coordinates?\b|\bevery (?:real |corresponding )?y\b|\by-intercepts?\b|\by[- ]axis\b|\bvalues? of y\b/i.test(ask) && !/\bx[- ]coordinates?\b|\bevery (?:real )?x\b|\bx-intercepts?\b|\bvalues? of x\b/i.test(ask) ? 'y' : 'x';
  }
  function answerHtml(a, problem) {
    if (a.display) return a.display;
    const decimal = !!problem && decimalContext(problem), fmtV = decimal ? fmtDec : fmtR;
    if (a.type === 'num' && a.tol) {
      // A printed answer must itself satisfy the authored tolerance. Fixed
      // hundredths made e.g. a thousandth key0.137 appear incorrectly as0.14.
      const digits = Math.max(0, Math.min(12, Math.ceil(-Math.log10(2 * a.tol) - 1e-10)));
      return fmtSetVal(setNum(a.value), digits) + (a.units ? ` ${a.units}` : '');
    }
    if (a.type === 'num') return fmtV(a.value) + (a.units ? ` ${a.units}` : '');
    if (a.type === 'expr' || a.type === 'box') return (a.lhs ? `<i>${a.lhs}</i> = ` : '') + fmtP(a.value);
    if (a.type === 'factored') return fmtFactors(a.factors);
    if (a.type === 'pair') return `(${fmtV(a.value[0])}, ${fmtV(a.value[1])})`;
    if (a.type === 'set') return a.values.length ? a.values.map(v => `<i>${a.var || setVariable(problem)}</i> = ${decimal && typeof v !== 'number' ? fmtDec(v) : fmtSetVal(v)}`).join(', ') : 'no solution';
    if (a.type === 'ineq') return fmtIneq(a.value, a.var);
    if (a.type === 'radical') return fmtRad(a.value);
    if (a.type === 'complex') return fmtComplex(a.value);
    if (a.type === 'ratexpr') return fmtRatExpr(a.value);
    if (a.type === 'vector') return fmtVector(a.value);
    if (a.type === 'matrix') return fmtMatrix(a.value);
    if (a.type === 'mc') return a.choices[a.index];
    return '';
  }
  const PARSE_HELP = 'I couldn’t read that. Type exponents with ^ (2x^2 − 3x + 1) and fractions with / (3/4).';
  function gradePolyInput(input, target, std) {
    if (typeof input === 'string') input = input.replace(/^\s*([a-z](_\{?[a-z0-9]+\}?)?|[a-z]\s*\(\s*[a-z]\s*\))\s*=\s*/i, '');
    let p;
    try { p = parsePoly(input); } catch (e) { return { status: 'invalid', note: PARSE_HELP }; }
    if (!P.eq(p, target)) return { status: 'wrong', parsed: p };
    if (std) {
      let sc; try { sc = standardCheck(input); } catch (e) { sc = { ok: false, reason: 'notSimplified' }; }
      if (!sc.ok) return { status: 'almost', reason: 'form', note: sc.reason === 'order' ? 'Those are the right terms. Now write them in standard form: highest degree first.' : 'That’s equivalent, but not simplified. Multiply everything out and combine like terms.' };
    }
    return { status: 'correct' };
  }
  function gradeRaw(problem, input) {
    if (typeof input === 'number' && Number.isFinite(input)) input = String(input);
    const a = problem.answer;
    // Characteristic polynomials are written in λ; L (or l) types it on any keyboard.
    if (a.lambda && typeof input === 'string') input = input.replace(/l/gi, 'λ');
    let res;
    if (a.type === 'mc') {
      if ((typeof input !== 'string' && typeof input !== 'number') || !String(input).trim()) return { status: 'invalid', note: 'Choose an answer before submitting.' };
      const i = Number(input);
      if (!Number.isInteger(i) || i < 0 || i >= a.choices.length) return { status: 'invalid', note: 'Choose one of the available answers.' };
      if (i === a.index) return { status: 'correct' };
      // Saved older questions may contain another valid ordering among equal-degree terms.
      if (problem.topic === 'std_form') {
        try {
          const selected=toText(a.choices[i]),target=toText(a.choices[a.index]);
          if(P.eq(parsePoly(selected),parsePoly(target))&&standardCheck(selected).ok)return {status:'correct'};
        } catch (e) { /* A malformed choice cannot earn credit. */ }
      }
      return { status: 'wrong', note: a.notes[i] || null };
    }
    if (a.type !== 'mc' && a.type !== 'box' && (typeof input !== 'string' || !input.trim() || input.length > 512)) return { status: 'invalid', note: 'Enter an answer first.' };
    if (a.type === 'num' && a.tol) {
      let f; try { f = evalNum(stripUnits(input)); } catch (e) { return { status: 'invalid', note: 'Enter a number, like 7, \u22123, 2/5 or 1.25.' }; }
      if (Math.abs(f - setNum(a.value)) <= a.tol + 1e-9) return { status: 'correct' };
      return withTrap(problem, { status: 'wrong' }, f);
    }
    if (a.type === 'num') {
      let v;
      try { v = parseNumber(input); } catch (e) { return { status: 'invalid', note: 'Enter a number, like 7, −3, 2/5 or 1.25.' }; }
      if (req(v, a.value)) {
        if (a.units && !unitsMatch(input, a.units)) return { status: 'almost', reason: 'units', note: LENGTH_UNITS.has(a.units) ? `The number is right. Give this length in ${a.units}, like (${v.n}) ${a.units}. Area uses square units; this answer is a length.` : `The number is right. Include the units: ${a.units}.` };
        return { status: 'correct' };
      }
      res = { status: 'wrong', parsed: v };
      for (const t of problem.traps || []) if (t.value && t.value.n !== undefined && req(t.value, v)) { res.note = t.note; break; }
      return res;
    }
    if (a.type === 'expr') {
      res = gradePolyInput(input, a.value, a.std);
      if (res.status === 'wrong') for (const t of problem.traps || []) if (t.value && t.value.n === undefined && P.eq(t.value, res.parsed)) { res.note = t.note; break; }
      return res;
    }
    if (a.type === 'box') {
      // input: {cells: [4 strings], final: string}
      if (!input || typeof input !== 'object' || !Array.isArray(input.cells) || input.cells.length !== 4 || typeof input.final !== 'string') return { status: 'invalid', note: 'Fill the four boxes and enter the final product.' };
      const cellOk = a.cells.map((c, k) => { try { return P.eq(parsePoly(input.cells[k] || ''), c); } catch (e) { return false; } });
      res = gradePolyInput(input.final || '', a.value, a.std);
      res.cells = cellOk;
      if (res.status === 'correct' && !cellOk.every(Boolean)) { res.status = 'almost'; res.reason = 'cells'; res.note = 'Your final answer is right. Check the boxes marked in red.'; }
      if (res.status === 'wrong') {
        for (const t of problem.traps || []) if (t.value && t.value.n === undefined && P.eq(t.value, res.parsed)) { res.note = t.note; break; }
        if (!res.note && !cellOk.every(Boolean)) res.note = 'Each box is its row term times its column term. ' + boxFeedback(a, cellOk);
      }
      return res;
    }
    if (a.type === 'factored') {
      let p; try { p = parsePoly(input); } catch (e) { return { status: 'invalid', note: PARSE_HELP }; }
      if (!P.eq(p, a.value)) { res = { status: 'wrong', parsed: p }; return withTrap(problem, res, p); }
      let fc; try { fc = factoredCheck(input); } catch (e) { fc = { ok: false, reason: 'notFactored' }; }
      if (fc.ok) return { status: 'correct' };
      return { status: 'almost', note: fc.reason === 'notCompletely' ? 'Equal, but not factored completely. One of your factors can still be factored: look for a common factor first, then check each trinomial.' : 'That’s equal, but it isn’t factored. Write it as a product, like 3(x + 2)(x − 5).' };
    }
    if (a.type === 'pair') {
      let v; try { v = parsePair(input); } catch (e) { return { status: 'invalid', note: 'Write an ordered pair, like (2, −3).' }; }
      if (req(v[0], a.value[0]) && req(v[1], a.value[1])) return { status: 'correct' };
      res = { status: 'wrong', parsed: v };
      if (req(v[0], a.value[1]) && req(v[1], a.value[0])) res.note = 'Those numbers are swapped: an ordered pair is (x, y), with x first.';
      return withTrap(problem, res, v);
    }
    if (a.type === 'set') {
      let v; try { v = parseSet(input); } catch (e) { return { status: 'invalid', note: 'List the solutions separated by commas, like x = 2, x = −5. Type "no solution" if there are none.' }; }
      if (setEq(v, a.values, a.tol || 1e-9)) return { status: 'correct' };
      res = { status: 'wrong', parsed: v };
      const want = a.values.map(setNum);
      if (v.length < want.length && v.every(x => want.some(w => Math.abs(w - x) <= (a.tol || 1e-9)))) res.note = `That’s ${v.length === 1 ? 'one of the solutions' : 'some of the solutions'}. There ${want.length === 2 ? 'are two' : 'are ' + want.length}: find them all.`;
      return withTrap(problem, res, v.map(x => x));
    }
    if (a.type === 'ineq') {
      let v; try { v = parseIneq(input, a.var); } catch (e) { return { status: 'invalid', note: 'Write it like x > 3, −2 < x ≤ 5, or x < −1 or x > 4. Use <= and >= for ≤ and ≥.' }; }
      if (ineqEq(v, a.value)) return { status: 'correct' };
      res = { status: 'wrong', parsed: v };
      if (v.length === a.value.length && v.every((iv, k) => { const w = ivSort(a.value)[k]; return ivKey({ ...iv, loInc: w.loInc, hiInc: w.hiInc }) === ivKey(w); })) res.note = 'Right boundary, wrong endpoint: check whether the boundary number is included (≤, ≥) or not (<, >).';
      return withTrap(problem, res, v);
    }
    if (a.type === 'radical') {
      let v; try { v = parseRadical(input); } catch (e) { return { status: 'invalid', note: 'Type roots as √ or sqrt, like 3√2 or 3sqrt(2).' }; }
      if (RAD.eq(v.value, a.value)) return v.simplified || !a.std ? { status: 'correct' } : { status: 'almost', note: 'That’s equal, but not in simplest form. Take out every perfect-square factor, combine like radicals, and leave no root in a denominator.' };
      return withTrap(problem, { status: 'wrong', parsed: v.value }, v.value);
    }
    if (a.type === 'complex') {
      let v; try { v = parseComplex(input); } catch (e) { return { status: 'invalid', note: 'Write a complex number as a + bi, like 3 − 2i, 4i or −5.' }; }
      if (req(v.value.re, a.value.re) && req(v.value.im, a.value.im)) return v.simplified ? { status: 'correct' } : { status: 'almost', reason: 'form', note: 'That’s equal, but not simplified. Replace i² with −1 and write the answer as a + bi.' };
      res = { status: 'wrong', parsed: v.value };
      if (req(v.value.re, a.value.re) && req(v.value.im, rmul(a.value.im, R(-1))) && a.value.im.n !== 0) res.note = 'The real part is right, but the imaginary part has the wrong sign. Remember that i² = −1.';
      return withTrap(problem, res, v.value);
    }
    if (a.type === 'ratexpr') {
      let v; try { v = parseRatExpr(input); } catch (e) { return { status: 'invalid', note: e instanceof ParseError && e.message === 'group' ? 'Put the whole numerator and the whole denominator in parentheses, like (x + 1)/(x − 2).' : 'Write it as (numerator)/(denominator), like (x + 1)/(x − 2), using ^ for exponents.' }; }
      if (ratEq(v, a.value)) {
        if (a.std && P.degree(v.den) > P.degree(a.value.den)) return { status: 'almost', reason: 'form', note: 'That’s equivalent, but it isn’t fully simplified. Factor the numerator and denominator and cancel every common factor.' };
        return { status: 'correct' };
      }
      return withTrap(problem, { status: 'wrong', parsed: v }, v);
    }
    if (a.type === 'vector') {
      let v; try { v = parseVector(input); } catch (e) { return { status: 'invalid', note: 'Write a vector as its components in parentheses, like (1, −2, 3).' }; }
      if (v.length !== a.value.length) return { status: 'wrong', parsed: v, note: `Your vector has ${v.length} component${v.length === 1 ? '' : 's'}, but this one has ${a.value.length}.` };
      if (vecEq(v, a.value, a.parallel)) return { status: 'correct' };
      res = { status: 'wrong', parsed: v };
      if (a.parallel && v.every(x => x.n === 0)) res.note = 'The zero vector never counts: give a nonzero vector.';
      else if (!a.parallel && vecEq(v, a.value.map(x => rmul(x, R(-1))))) res.note = 'Every component has the wrong sign.';
      return withTrap(problem, res, v);
    }
    if (a.type === 'matrix') {
      let v; try { v = parseMatrix(input); } catch (e) { return { status: 'invalid', note: 'Type the matrix row by row: commas or spaces between entries and a semicolon between rows, like [1, 2; 3, 4].' }; }
      const rows = a.value.length, cols = a.value[0].length;
      if (v.length !== rows || v[0].length !== cols) return withTrap(problem, { status: 'wrong', parsed: v, note: `Your matrix is ${v.length} × ${v[0].length}, but the answer is ${rows} × ${cols} (rows × columns).` }, v);
      if (matEq(v, a.value)) return { status: 'correct' };
      res = { status: 'wrong', parsed: v };
      if (rows === cols && matEq(v, a.value[0].map((_, j) => a.value.map(r => r[j])))) res.note = 'Those are the right numbers, but rows and columns are swapped: that is the transpose of the answer.';
      return withTrap(problem, res, v);
    }
    return { status: 'invalid' };
  }

  /* ---------------- vectors and matrices ----------------
   * A vector is an array of rationals; a matrix is an array of equal-length rows.
   * Students type vectors as (1, -2, 3) and matrices row by row as [1, 2; 3, 4].
   * A vector answer with `parallel` accepts any nonzero multiple (eigenvectors, normals). */
  function vecEq(x, y, parallel) {
    if (!Array.isArray(x) || !Array.isArray(y) || x.length !== y.length) return false;
    if (!parallel) return x.every((v, k) => req(v, y[k]));
    if (x.every(v => v.n === 0) || y.every(v => v.n === 0)) return false;
    // Proportional: every 2×2 cross product vanishes.
    for (let i = 0; i < x.length; i++) for (let j = i + 1; j < x.length; j++) if (!req(rmul(x[i], y[j]), rmul(x[j], y[i]))) return false;
    return true;
  }
  function matEq(x, y) {
    return Array.isArray(x) && Array.isArray(y) && x.length === y.length && x.every((r, i) => Array.isArray(r) && r.length === y[i].length && r.every((v, j) => req(v, y[i][j])));
  }
  function splitEntries(row) {
    const t = row.trim().replace(/-\s+(?=[\d(.])/g, '-');
    if (!t || t.length > 4000) throw new ParseError('empty or too long');
    const entries = (t.includes(',') ? t.split(',') : t.split(/\s+/)).map(e => e.trim());
    if (entries.some(e => !e)) throw new ParseError('empty entry');
    return entries.map(e => parseNumber(e));
  }
  function parseVector(src) {
    let s = String(src).replace(/[−–—]/g, '-').replace(/[⟨〈]/g, '<').replace(/[⟩〉]/g, '>').trim().toLowerCase();
    s = s.replace(/^[a-z](?:_?\w)?\s*=\s*/, '');
    const m = /^[([<]\s*([\s\S]*?)\s*[)\]>]$/.exec(s);
    if (m) s = m[1];
    if (/[;[\]]/.test(s)) throw new ParseError('vector');
    const v = splitEntries(s);
    if (v.length < 2) throw new ParseError('vector');
    return v;
  }
  function parseMatrix(src) {
    let s = String(src).replace(/[−–—]/g, '-').trim();
    s = s.replace(/^[a-z]\w*\s*=\s*/i, '');
    if (s.length > 4000) throw new ParseError('matrix too long');
    let rows;
    if (/^\[\s*\[/.test(s)) {
      if (!/^\[\s*\[[^[\]]+\](?:\s*,\s*\[[^[\]]+\])*\s*\]$/.test(s)) throw new ParseError('matrix');
      rows = [...s.slice(1, -1).matchAll(/\[([^[\]]*)\]/g)].map(m => m[1]);
    } else {
      const m = /^[[(]\s*([\s\S]*?)\s*[\])]$/.exec(s);
      if (m) s = m[1];
      rows = s.split(/\s*(?:;|\n|\|)\s*/);
      if (rows.some(r => !r.trim())) throw new ParseError('empty row');
    }
    if (!rows.length) throw new ParseError('matrix');
    const out = rows.map(splitEntries);
    if (out.some(r => r.length !== out[0].length)) throw new ParseError('ragged');
    return out;
  }
  const typeEntry = r => (r.d === 1 ? String(r.n) : r.n + '/' + r.d);
  function typeVector(v) { return '(' + v.map(typeEntry).join(', ') + ')'; }
  function typeMatrix(m) { return '[' + m.map(r => r.map(typeEntry).join(', ')).join('; ') + ']'; }
  function fmtVector(v) { return '(' + v.map(fmtR).join(', ') + ')'; }
  // A bracketed grid. Inline styles keep it self-contained wherever problem HTML is shown.
  function fmtMatrix(m, opts = {}) {
    const cols = m[0].length, bar = opts.bar;
    const cell = (x, j) => `<span style="padding:0.1em 0.45em;text-align:center${bar !== undefined && j === bar ? ';border-left:1px solid currentColor' : ''}">${typeof x === 'string' ? x : fmtR(x)}</span>`;
    const side = edge => `<span aria-hidden="true" style="width:0.3em;border:2px solid currentColor;border-${edge}:0;align-self:stretch"></span>`;
    const grid = `<span style="display:inline-grid;grid-template-columns:repeat(${cols},auto);align-items:center">${m.map(r => r.map(cell).join(' ')).join(' ')}</span>`;
    return `<span class="la-matrix" role="math" style="display:inline-flex;align-items:stretch;vertical-align:middle;margin:0 0.15em;padding:0.1em 0">${side('right')}${grid}${side('left')}</span>`;
  }

  /* ---------------- end vectors and matrices ---------------- */

  /* ---------------- complex numbers a + bi ----------------
   * A complex value is {re: R, im: R}. Students type it as a polynomial in i;
   * powers of i are reduced, and an unreduced power (i² left in) is "almost". */
  const IPOW = [[1, 0], [0, 1], [-1, 0], [0, -1]];
  function parseComplex(src) {
    let s = String(src).trim().replace(/^[a-z]\s*=\s*/i, '');
    if (!s || s.length > 200) throw new ParseError('empty');
    const p = parsePoly(s);
    let re = R(0), im = R(0), simplified = true;
    for (const k in p) {
      const t = p[k], vars = Object.keys(t.m);
      if (vars.some(v => v !== 'i')) throw new ParseError('only i');
      const e = t.m.i || 0;
      if (e > 1) simplified = false;
      const [a, b] = IPOW[e % 4];
      re = radd(re, rmul(t.c, R(a))); im = radd(im, rmul(t.c, R(b)));
    }
    return { value: { re, im }, simplified };
  }
  function typeComplex(v) {
    const re = v.re, im = v.im, num = r => (r.d === 1 ? String(Math.abs(r.n)) : Math.abs(r.n) + '/' + r.d);
    if (im.n === 0) return (re.n < 0 ? '-' : '') + num(re);
    const imTxt = (im.n === 1 && im.d === 1) || (im.n === -1 && im.d === 1) ? 'i' : (im.d === 1 ? num(im) + 'i' : '(' + num(im) + ')i');
    if (re.n === 0) return (im.n < 0 ? '-' : '') + imTxt;
    return (re.n < 0 ? '-' : '') + num(re) + (im.n < 0 ? ' - ' : ' + ') + imTxt;
  }
  function fmtComplex(v) {
    const re = v.re, im = v.im, abs = r => R(Math.abs(r.n), r.d);
    const imHtml = () => { const a = abs(im); return a.n === 1 && a.d === 1 ? '<i>i</i>' : fmtR(a) + '<i>i</i>'; };
    if (im.n === 0) return fmtR(re);
    if (re.n === 0) return (im.n < 0 ? MINUS : '') + imHtml();
    return fmtR(re) + (im.n < 0 ? ' ' + MINUS + ' ' : ' + ') + imHtml();
  }

  /* ---------------- rational expressions ----------------
   * A value is {num, den}: two polynomials. Two answers are equal when
   * num1·den2 = num2·den1. Students type (numerator)/(denominator). */
  function ratEq(x, y) { return P.eq(P.mul(x.num, y.den), P.mul(y.num, x.den)); }
  function parseRatExpr(src) {
    let s = normalize(String(src)).trim().replace(/^\s*([a-z]\s*\(\s*[a-z]\s*\)|[a-z])\s*=\s*/i, '');
    if (!s || s.length > 300) throw new ParseError('empty');
    // Find a top-level "/" whose right side starts a parenthesized group or a variable.
    let depth = 0, cut = -1;
    for (let k = 0; k < s.length; k++) {
      const ch = s[k];
      if (ch === '(') depth++;
      else if (ch === ')') depth--;
      else if (ch === '/' && depth === 0) { const rest = s.slice(k + 1).trimStart(); if (rest.startsWith('(') || /^[a-z]/.test(rest)) { cut = k; break; } }
    }
    if (cut < 0) { const num = parsePoly(s); return { num, den: P.c(1) }; }
    const left = s.slice(0, cut).trim(), right = s.slice(cut + 1).trim();
    const topLevelSum = t => { let d = 0; for (let k = 0; k < t.length; k++) { const c = t[k]; if (c === '(') d++; else if (c === ')') d--; else if (d === 0 && (c === '+' || c === '-') && k > 0 && !/[\^*/(]/.test(t.slice(0, k).trim().slice(-1))) return true; } return false; };
    if (topLevelSum(left) || topLevelSum(right)) throw new ParseError('group');
    const num = parsePoly(left), den = parsePoly(right);
    if (!Object.keys(den).length) throw new ParseError('zero denominator');
    return { num, den };
  }
  function typeRatExpr(v) { return P.isConst(v.den) && req(P.constVal(v.den), R(1)) ? typeP(v.num) : `(${typeP(v.num)})/(${typeP(v.den)})`; }
  function fmtRatExpr(v) { return P.isConst(v.den) && req(P.constVal(v.den), R(1)) ? fmtP(v.num) : frac(fmtP(v.num), fmtP(v.den)); }
  function withTrap(problem, res, parsed) {
    if (!res.note) for (const t of problem.traps || []) { let hit = false; try { hit = valuesEqual(problem.answer, parsed, t.value); } catch (e) { hit = false; } if (hit) { res.note = t.note; break; } }
    return res;
  }
  // Encode a value of an answer's type as text a student could type.
  function encodeValue(a, v) {
    switch (a.type) {
      case 'num': return typeof v === 'number' ? String(Math.round(v * 1e6) / 1e6) : v.d === 1 ? String(v.n) : v.n + '/' + v.d;
      case 'expr': case 'factored': return typeP(v);
      case 'pair': return `(${encodeValue({ type: 'num' }, v[0])}, ${encodeValue({ type: 'num' }, v[1])})`;
      case 'set': return v.length ? v.map(x => encodeValue({ type: 'num' }, typeof x === 'number' ? x : x)).join(', ') : 'no solution';
      case 'ineq': return fmtIneq(v, a.var, true);
      case 'radical': return typeRad(v);
      case 'complex': return typeComplex(v);
      case 'ratexpr': return typeRatExpr(v);
      case 'vector': return typeVector(v);
      case 'matrix': return typeMatrix(v);
    }
    return String(v);
  }
  function grade(problem, input) {
    const result = gradeRaw(problem, input);
    if (result.status === 'wrong' && !result.note) result.note = fallbackFeedback(problem, result.parsed);
    if (result.status === 'correct') result.note = 'Correct. You can review the method and common pitfalls below.';
    return result;
  }
  // A string a student could type that should be graded correct (used by the harness).
  function sampleInput(problem) {
    const a = problem.answer;
    if (a.type === 'mc') return String(a.index);
    if (a.type === 'num' && a.tol) return encodeValue(a, setNum(a.value)) + (a.units ? ' ' + a.units : '');
    if (a.type === 'num') return (a.value.d === 1 ? String(a.value.n) : a.value.n + '/' + a.value.d) + (a.units ? ' ' + a.units : '');
    if (a.type === 'expr') return typeP(a.value);
    if (a.type === 'box') return { cells: a.cells.map(typeP), final: typeP(a.value) };
    if (a.type === 'factored') return typeFactors(a.factors);
    if (a.type === 'set') return encodeValue(a, a.values);
    return encodeValue(a, a.value);
  }

  /* ---------------- dates ---------------- */
  function dstr(d) { const y = d.getFullYear(), m = d.getMonth() + 1, dd = d.getDate(); return `${y}-${m < 10 ? '0' : ''}${m}-${dd < 10 ? '0' : ''}${dd}`; }
  function today() { return dstr(new Date()); }
  function parseD(s) { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d, 12); }
  function addDays(s, n) { const d = parseD(s); d.setDate(d.getDate() + n); return dstr(d); }
  function daysBetween(a, b) { return Math.round((parseD(b) - parseD(a)) / 86400000); }

  /* ---------------- student model + scheduler ---------------- */
  const MASTERY_STREAK = 3;
  const LESSON_CAP = 12;
  const INTERVALS = [2, 4, 8, 16, 32, 64];
  const CRAM_INTERVALS = [1, 2, 4, 8, 16, 32];
  function interval(ts) { const list = ts.crammed ? CRAM_INTERVALS : INTERVALS; return list[Math.min(Math.max(ts.reps - 1, 0), list.length - 1)]; }
  function newStudent(name) { return { name, created: today(), topics: {}, xp: 0, log: [], test: null, rev: 0 }; }
  function upgradeStudent(st) {
    let changed = false;
    for (const s of Object.values(st.topics || {})) {
      if (s.source === 'placement' && s.status === 'mastered' && !s.evidence) {
        s.status = 'new'; s.due = null; s.streak = 0; s.evidence = 'legacy-unverified'; changed = true;
      }
    }
    if (changed) { if (st.test) st.test.placementDone = false; st.placementRecheck = true; }
    return changed;
  }
  function ts(st, id) {
    if (!st.topics[id]) st.topics[id] = { status: 'new', streak: 0, attempts: 0, correct: 0, reps: 0, due: null, crammed: false, source: null, lessonTries: 0, flagged: false };
    return st.topics[id];
  }
  const mastered = (st, id) => !!(st.topics[id] && st.topics[id].status === 'mastered');
  function ancestors(id, acc) {
    acc = acc || new Set();
    for (const p of byId[id].prereqs) if (!acc.has(p)) { acc.add(p); ancestors(p, acc); }
    return acc;
  }
  deferOrder = false; reorder();
  const topo = ids => ids.slice().sort((a, b) => ORDER[a] - ORDER[b]);
  function closure(targets) { const s = new Set(targets); for (const t of targets) ancestors(t, s); return topo([...s]); }
  function testActive(st, day) { return !!(st.test && day <= st.test.date); }

  function logAttempt(st, id, ok, kind, day) {
    st.log.push({ d: day, t: id, ok: ok ? 1 : 0, k: kind });
    if (st.log.length > 400) st.log.splice(0, st.log.length - 400);
    const s = ts(st, id); s.attempts++; if (ok) s.correct++;
    if (ok && kind !== 'placement') st.xp += kind === 'lesson' ? 10 : kind === 'quiz' ? 15 : 12;
  }
  function master(st, id, day, opts) {
    const s = ts(st, id);
    s.status = 'mastered'; s.reps = 1; s.streak = 0; s.flagged = false;
    s.crammed = !!(opts && opts.crammed); s.source = (opts && opts.source) || 'lesson';
    s.masteredOn = day;
    s.evidence = 'direct'; s.lastReviewOn = day; s.reviewDays = 0;
    s.due = addDays(day, opts && opts.firstGap ? opts.firstGap : interval(s));
  }
  // Disabled until reviewed practiced-subskill mappings exist. Prerequisite
  // ancestry alone does not establish that a generated question practised it.
  function implicitCredit(st, id, day) {
    return;
  }
  // Lesson: returns {mastered, gaveUp}
  function recordLesson(st, id, ok, day) {
    if (!byId[id] || typeof ok !== 'boolean') throw new Error('Invalid lesson evidence');
    if (!byId[id].prereqs.every(p => mastered(st, p))) throw new Error('Lesson prerequisites need checking first');
    const s = ts(st, id);
    if (s.status === 'new') s.status = 'learning';
    s.lessonTries++;
    logAttempt(st, id, ok, 'lesson', day);
    s.streak = ok ? s.streak + 1 : 0;
    if (s.streak >= MASTERY_STREAK) { master(st, id, day, { crammed: testActive(st, day) }); return { mastered: true }; }
    if (s.lessonTries % LESSON_CAP === 0) { s.flagged = true; return { gaveUp: true }; }
    return {};
  }
  // Review of two questions: results = [bool, bool]
  function recordReview(st, id, results, day, kind) {
    if (!Array.isArray(results) || !results.length || results.some(v => typeof v !== 'boolean')) throw new Error('Review needs actual answer results');
    if (!mastered(st, id)) throw new Error('Use instruction or assessment for an unlearned skill');
    const s = ts(st, id);
    results.forEach(ok => logAttempt(st, id, ok, kind || 'review', day));
    const right = results.filter(Boolean).length;
    if (right === results.length) {
      if (results.length >= 2 && day > (s.lastReviewOn || s.masteredOn || '') && (!s.due || day >= s.due)) {
        s.reps++; s.reviewDays = (s.reviewDays || 0) + 1; s.lastReviewOn = day; s.due = addDays(day, interval(s));
      }
    }
    else if (right === 0 && results.length > 1) { s.status = 'learning'; s.streak = 0; s.reps = 0; s.due = null; s.flagged = true; }
    else { s.due = addDays(day, 1); }
  }
  function recordQuizTopic(st, id, results, day) {
    if (mastered(st, id)) return recordReview(st, id, results, day, 'quiz');
    if (!Array.isArray(results) || !results.length || results.some(v => typeof v !== 'boolean')) throw new Error('Quiz needs actual answer results');
    results.forEach(ok => logAttempt(st, id, ok, 'quiz', day));
    if (results.some(ok => !ok)) ts(st, id).assessmentNeedsReview = true;
  }

  /* ---------------- placement (adaptive descent from the test topics) ---------------- */
  function placementStart(st) {
    const targets = st.test ? st.test.targets.filter(t => !mastered(st, t)) : [];
    return { stack: topo(targets), seen: [], asked: 0, max: 30 };
  }
  function placementNext(st, pl) {
    while (pl.stack.length && pl.asked < pl.max) {
      const id = pl.stack.pop();
      if (pl.seen.includes(id) || mastered(st, id)) continue;
      pl.seen.push(id);
      return id;
    }
    return null;
  }
  function placementRecord(st, pl, id, results, day) {
    if (!byId[id] || !Array.isArray(results) || !results.length || results.length > 2 || results.some(v => typeof v !== 'boolean') || pl.asked + results.length > pl.max) throw new Error('Invalid placement evidence');
    pl.asked += results.length;
    results.forEach(ok => logAttempt(st, id, ok, 'placement', day));
    if (results.length >= 2 && results.every(Boolean)) {
      // Direct evidence for this topic only. Sample foundations separately.
      master(st, id, day, { source: 'placement', firstGap: 3 });
    } else {
      for (const p of topo(byId[id].prereqs).reverse()) if (!mastered(st, p) && !pl.seen.includes(p)) pl.stack.push(p);
    }
  }

  /* ---------------- planning ---------------- */
  function plan(st, day) {
    if (!st.test) return null;
    const t = st.test;
    const daysLeft = daysBetween(day, t.date);
    const all = closure(t.targets);
    const needed = all.filter(id => !mastered(st, id));
    const studyDays = Math.max(1, daysLeft); // days before test day, today included
    const lessonDays = daysLeft >= 2 ? studyDays - 1 : studyDays; // keep the eve for the practice test
    const perDay = needed.length ? Math.max(1, Math.ceil(needed.length / lessonDays)) : 0;
    const schedule = [];
    for (let k = 0; k < studyDays; k++) {
      const date = addDays(day, k);
      const lessons = needed.slice(k * perDay, (k + 1) * perDay);
      const isEve = date === addDays(t.date, -1);
      schedule.push({ date, lessons: isEve && daysLeft >= 2 ? [] : lessons, practiceTest: isEve });
    }
    if (daysLeft >= 2) { const spill = needed.slice(lessonDays * perDay); if (spill.length) schedule[schedule.length - 1].lessons = spill; }
    const status = daysLeft < 0 ? 'past' : perDay <= 3 ? 'on track' : perDay <= 5 ? 'busy' : 'tight';
    const targetsMastered = t.targets.filter(id => mastered(st, id)).length;
    return { daysLeft, all, needed, perDay, schedule, status, targetsMastered, targetsTotal: t.targets.length, placementDone: !!t.placementDone };
  }

  // Today's queue of tasks: [{type: 'placement'|'lesson'|'review'|'quiz', topic?}]
  function buildSession(st, day) {
    const tasks = [];
    const dueList = TOPICS.map(t => t.id).filter(id => { const s = st.topics[id]; return s && s.status === 'mastered' && s.due && s.due <= day; })
      .sort((a, b) => st.topics[a].due < st.topics[b].due ? -1 : 1);
    if (testActive(st, day)) {
      const pl = plan(st, day);
      if (pl.daysLeft === 0) return st.test.targets.filter(id => mastered(st, id)).slice(0, 2).map(topic => ({ type: 'review', topic, light: true }));
      if (!st.test.placementDone && pl.needed.length) return [{ type: 'placement' }];
      const todayPlan = pl.schedule[0] || { lessons: [], practiceTest: false };
      const inScope = new Set(pl.all);
      for (const id of dueList.filter(id => inScope.has(id)).slice(0, 4)) tasks.push({ type: 'review', topic: id });
      for (const id of todayPlan.lessons) tasks.push({ type: 'lesson', topic: id });
      // Learning topics left over from earlier days come first among lessons.
      for (const id of pl.needed) if (st.topics[id] && st.topics[id].status === 'learning' && !todayPlan.lessons.includes(id)) tasks.unshift({ type: 'lesson', topic: id });
      if (todayPlan.practiceTest || pl.daysLeft === 0 || (pl.needed.length === 0 && !tasks.length)) tasks.push({ type: 'quiz' });
      else {
        const mixed = st.test.targets.filter(id => mastered(st, id) && !dueList.includes(id));
        for (const id of mixed.slice(-3)) tasks.push({ type: 'review', topic: id, light: true });
      }
      return dedupe(tasks);
    }
    for (const id of dueList.slice(0, 5)) tasks.push({ type: 'review', topic: id });
    const frontier = TOPICS.map(t => t.id).filter(id => !mastered(st, id) && byId[id].prereqs.every(p => mastered(st, p)));
    for (const id of frontier.slice(0, 2)) tasks.push({ type: 'lesson', topic: id });
    return dedupe(tasks);
  }
  function dedupe(tasks) { const seen = new Set(); return tasks.filter(t => { const k = t.type + ':' + (t.topic || ''); if (seen.has(k)) return false; seen.add(k); return true; }); }

  // Two per target, in shuffled rounds. A cap must not silently omit coverage.
  function quizTopics(st, rng) {
    const H = helpers(rng || Math.random);
    const ids = st.test ? st.test.targets.slice() : TOPICS.map(t => t.id);
    const first = H.shuffle(ids), second = H.shuffle(ids);
    if (ids.length > 1 && first[first.length - 1] === second[0]) second.push(second.shift());
    return first.concat(second);
  }

  // After a test passes: crammed topics come due soon so they stick.
  function endTestIfPast(st, day) {
    if (st.test && day > st.test.date) {
      for (const id in st.topics) { const s = st.topics[id]; if (s.status === 'mastered' && s.crammed && s.due > addDays(day, 1)) s.due = addDays(day, 1); }
      st.pastTests = (st.pastTests || []).concat([{ title: st.test.title, date: st.test.date }]);
      st.test = null;
      return true;
    }
    return false;
  }

  // The teacher (Oct 2026): mostly multiplying polynomials, a few add/subtract questions, and
  // shaded-area applications. Exponent properties and classifying are used inside other questions,
  // not tested on their own, so they stay prerequisites rather than targets. Weights are questions
  // per school-test practice set (26 in all, matching the class practice test).
  const TEST3 = {
    title: 'Test 3: Multiplying Polynomials & Geometry Application',
    targets: ['exp_product', 'poly_add', 'poly_sub', 'mono_poly', 'binom_tiles', 'binom_box', 'binom_dist', 'poly_mult', 'special_sq', 'special_diff', 'geo_expr', 'geo_shaded', 'geo_solve'],
    weights: { exp_product: 1, poly_add: 1, poly_sub: 1, mono_poly: 2, binom_tiles: 1, binom_box: 2, binom_dist: 3, poly_mult: 3, special_sq: 2, special_diff: 2, geo_expr: 2, geo_shaded: 4, geo_solve: 2 },
  };

  const API = {
    R, P, poly, lin, parsePoly, parseNumber, standardCheck, fmtP, fmtR, toText, typeP, jsP,
    UNITS, TOPICS, byId, ORDER, closure, ancestors, topo,
    generate, generateFromSeed, grade, sampleInput, answerHtml, mulberry32, helpers, decimalText, decimalContext, decimalizeHtml, presentDecimals,
    today, addDays, daysBetween,
    newStudent, upgradeStudent, ts, mastered, recordLesson, recordReview, recordQuizTopic, master,
    placementStart, placementNext, placementRecord,
    plan, buildSession, quizTopics, endTestIfPast, testActive,
    MASTERY_STREAK, LESSON_CAP, TEST3,
    // building blocks for unit modules
    topic, unit, addPrereq, reorder, mc, frac, MINUS, fmtN, par, fmtTerms, fmtFactor, joinP, monoKey, monoDeg,
    gcd, lcm, radd, rsub, rmul, rdiv, req, rval, rpow, tilesSVG, rectSVG, triSVG, svgText,
    evalNum, stripUnits, RAD, sqrtSplit, parseRadical, fmtRad, typeRad, ROOT,
    parseIneq, ineqEq, ineqContains, fmtIneq, rayFor, numberLineSVG, parsePair, parseSet, setEq, fmtSetVal,
    factoredCheck, stillFactorable, fmtFactors, typeFactors, graphSVG, tableHTML,
    valuesEqual, encodeValue, answerValue,
    parseComplex, typeComplex, fmtComplex, parseRatExpr, typeRatExpr, fmtRatExpr, ratEq, normalize,
    parseVector, parseMatrix, typeVector, typeMatrix, fmtVector, fmtMatrix, vecEq, matEq,
  };
  return API;
}
const Algebra = createAlgebraEngine();
export default Algebra;
